import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl, tokenStore } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSyncWatch } from "../context/SyncContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Folder, Briefcase, Building2, Search, FileText, Trash, Upload, Package2, AlertCircle, LayoutGrid, List as ListIcon, RotateCcw, History } from "lucide-react";
import { PROJECT_CATEGORIES, TEAM_COLORS, getCategoryLabel } from "../lib/constants";
import { REGIE_COLORS, REGIE_COLOR_FAMILIES } from "./planificator/utils";
import ScopeBuilder from "../components/ScopeBuilder";
import QuickScopeInput from "../components/QuickScopeInput";
import { DocumentViewer } from "../components/DocumentViewer";
import { UploadButton } from "../components/AuthImg";
import { ProjectUtilitiesSection } from "../components/ProjectUtilitiesSection";
import { useConfirm } from "../components/ConfirmProvider";
import { fmtRoDate, fmtRoDateTime } from "../lib/dateFmt";
import DismantleDialog from "./planificator/DismantleDialog";
import { ProjectListRow, HoursReportDialog, ProjectDocsDialog, PipelineBanner, ProjectDetailDialog, RegieDetailDialog } from "./projects/ProjectSubcomponents";

// Câmpuri esențiale comune pe toate proiectele
const ESSENTIAL_PROJECT_FIELDS = [
  { key: "adresa", label: "Adresă șantier" },
  { key: "auftraggeber", label: "Auftraggeber" },
  { key: "min_people", label: "Minim personal" },
];

// Câmpuri obligatorii doar pentru proiectele care au execuție planificată (cat ≠ 1, 4)
const EXECUTION_PROJECT_FIELDS = [
  { key: "start_date", label: "Data start" },
  { key: "end_date", label: "Data finalizare" },
];

export function missingProjectFields(project) {
  if (!project) return [];
  const cat = String(project?.category || "");
  const isOfferStage = cat === "1";  // de ofertat
  const isRefused = cat === "4";
  const fields = [...ESSENTIAL_PROJECT_FIELDS];
  // Date de start/end nu sunt obligatorii la stage de ofertare/refuzat
  if (!isOfferStage && !isRefused) {
    fields.push(...EXECUTION_PROJECT_FIELDS);
  }
  // Detalii contact bauleiter – util la stage de ofertat și după
  if (cat !== "4") {
    if (!project.bauleiter && !project.bauherr) {
      // ok, măcar unul din ei
    }
  }
  const missing = fields.filter((f) => {
    const v = project[f.key];
    if (f.key === "min_people") return !v || Number(v) <= 0;
    return !v || String(v).trim() === "";
  });
  // Pentru cat 1 (de ofertat): deadline obligatoriu
  if (isOfferStage && !project.oferta_deadline) {
    missing.push({ key: "oferta_deadline", label: "Deadline ofertare" });
  }
  // Pentru cat 2 (ofertat) sau 3 (acceptat): trebuie fie fișier ofertă FIE preț
  if ((cat === "2" || cat === "3") && !project.oferta_file_id && (!project.oferta_price || project.oferta_price <= 0)) {
    missing.push({ key: "oferta_file_or_price", label: "Fișier ofertă SAU preț" });
  }
  // Pentru cat 5 (anmelduit SUVA): trebuie fișier SUVA Meldung
  if (cat === "5" && !project.suva_meldung_file_id) {
    missing.push({ key: "suva_meldung_file", label: "📎 SUVA Meldung" });
  }
  return missing;
}

// Câmpuri esențiale pentru o regie
const ESSENTIAL_REGIE_FIELDS = [
  { key: "firma", label: "Firmă" },
  { key: "adresa", label: "Adresă" },
  { key: "start_date", label: "Data start" },
  { key: "default_min_people", label: "Minim personal" },
];

export function missingRegieFields(regie) {
  if (!regie) return [];
  const missing = ESSENTIAL_REGIE_FIELDS.filter((f) => {
    const v = regie[f.key];
    if (f.key === "default_min_people") return !v || Number(v) <= 0;
    return !v || String(v).trim() === "";
  });
  // end_date e obligatoriu DOAR dacă regia nu e marcată ca "indeterminat"
  if (!regie.indeterminate_end && (!regie.end_date || String(regie.end_date).trim() === "")) {
    missing.push({ key: "end_date", label: "Data finalizare (sau marchează nedeterminat)" });
  }
  return missing;
}

export const DOC_CATEGORIES = [
  { code: "outlook", label: "Conversații Outlook", icon: "📧" },
  { code: "bericht", label: "Bericht clădire", icon: "🏢" },
  { code: "sanierungskonzept", label: "Sanierungskonzept", icon: "🛠️" },
  { code: "vdi_messung", label: "VDI Messung", icon: "📊" },
  { code: "suva_meldung", label: "SUVA Meldung", icon: "🛡️" },
  { code: "poze", label: "Poze", icon: "📷" },
  { code: "altele", label: "Altele (UGZ, Sanierungsbestätigung, SUVA Abmeldung etc.)", icon: "📂" },
];

const EMPTY_PROJ = {
  name: "", category: "1", status: "În lucru",
  start_date: "", end_date: "", indeterminate_start: false,
  auftraggeber: "", auftraggeber_phone: "", auftraggeber_contact: "",
  bauherr: "", bauherr_phone: "",
  bauleiter: "", bauleiter_phone: "",
  adresa: "",
  scope_items: [],
  quick_scope: null,
  is_renovation: true,
  zones: [],
  min_people: 2, max_people: "",
  special: "", contact_email: "",
  utilities: null,
  vorarbeiter: "",
  description: "",
  oferta_deadline: "",
  color: "",
  is_ruckbau: false,
  requires_post_measurement: null, // null = neimpus încă (default — UI calculează la save)
};
// Culori predefinite pentru proiecte (cerere user)
const PROJECT_COLORS = [
  { code: "blue",   hex: "#3B82F6", label: "Albastru" },
  { code: "red",    hex: "#EF4444", label: "Roșu" },
  { code: "green",  hex: "#10B981", label: "Verde" },
  { code: "yellow", hex: "#F59E0B", label: "Galben" },
  { code: "purple", hex: "#8B5CF6", label: "Mov" },
  { code: "gray",   hex: "#6B7280", label: "Gri" },
];
const EMPTY_REGIE = {
  name: "", firma: "", adresa: "", vorarbeiter: "", report_frequency: "saptamanal", color: REGIE_COLORS[0], active: true, note: "",
  start_date: "", end_date: "", indeterminate_end: false,
  default_min_people: "", default_max_people: "",
  // Necesar zilnic opțional — 7 valori pentru Lu-Du, sau toate null = folosește săptămânal
  default_min_people_by_day: [null, null, null, null, null, null, null],
};

// Categorii „pe rol": de la 1 De Ofertat (inclusiv) până înainte de finalizare/demontare — active, nefinalizate
export const PE_ROL_CATS = ["1", "1.2", "2", "3", "5", "6"];

// Filtru pe status: in_lucru | de_demontat | demontat | toate
// Acum aliniat cu categoriile:
//   in_lucru = cat 6 (Sanierungskonzept)
//   de_demontat = cat 6.1 (Finalizat de demontat)
//   demontat = cat 7 (Demontat de Facturat)
// Alte categorii (1, 1.2, 2, 3, 4, 5, 8, 9, 10, 11) → bucket "other" — vizibile doar în "Toate".
export function statusBucket(p) {
  const cat = String(p.category || "").trim();
  if (cat === "6") return "in_lucru";
  if (cat === "6.1") return "de_demontat";
  if (cat === "7") return "demontat";
  return "other";
}

export default function Proiecte() {
  const { canDo, user } = useAuth();
  const confirm = useConfirm();
  const isPlanificator = user?.role === "planificator";
  const [rows, setRows] = useState([]);
  const [regii, setRegii] = useState([]);
  // Section tab: "projects" | "regii" | "both" — permite utilizatorului să vadă separat
  const [sectionTab, setSectionTab] = useState(() => {
    try { return localStorage.getItem("proiecte.section") || "projects"; } catch { return "projects"; }
  });
  useEffect(() => {
    try { localStorage.setItem("proiecte.section", sectionTab); } catch { /* noop */ }
  }, [sectionTab]);
  const [duplicateDismissals, setDuplicateDismissals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_PROJ);
  const [editing, setEditing] = useState(null);
  // ===== Auto-save draft pentru proiect nou =====
  // Persistăm `form` în localStorage când dialogul e deschis pentru un proiect NOU (nu la edit).
  // La închiderea accidentală (click în afară / refresh) draft-ul rămâne și se restaurează la următoarea deschidere.
  const DRAFT_KEY = "convic.proj.new.draft.v1";
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  // Restore draft când utilizatorul deschide dialog-ul pentru proiect nou
  useEffect(() => {
    if (!open || editing) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      // dacă draft-ul are conținut, îl restaurăm peste form-ul gol
      const hasContent = Object.values(data || {}).some((v) => typeof v === "string" ? v.trim() : Array.isArray(v) ? v.length : !!v);
      if (hasContent) {
        setForm({ ...EMPTY_PROJ, ...data });
        setDraftSavedAt(new Date());
        toast("📝 Am restaurat ciorna proiectului nesalvat", { duration: 2500 });
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);
  // Debounced auto-save în localStorage când form-ul se modifică
  useEffect(() => {
    if (!open || editing) return;
    const t = setTimeout(() => {
      try {
        // Verificăm dacă form-ul are conținut (altfel nu salvăm gol peste un draft eventual existent)
        const hasContent = Object.entries(form).some(([k, v]) => {
          if (k === "category") return v && v !== EMPTY_PROJ.category;
          if (typeof v === "string") return v.trim().length > 0;
          if (Array.isArray(v)) return v.length > 0;
          return false;
        });
        if (hasContent) {
          localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
          setDraftSavedAt(new Date());
        }
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form, open, editing]);
  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    setDraftSavedAt(null);
  };
  const [filterCat, setFilterCat] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);   // user-clicked card (blue border)
  const scrollPosRef = useRef(0);                               // saved scroll position

  // Save scroll position onMouseDown (fires BEFORE click / focus / state change).
  const saveScroll = () => {
    scrollPosRef.current = window.scrollY || window.pageYOffset || 0;
  };
  const restoreScroll = () => {
    const y = scrollPosRef.current || 0;
    if (y <= 5) return;
    // Radix Dialog captures body scroll onOpen and restores it onClose, but our
    // `saveScroll()` must have fired BEFORE Radix snapshots. In some cases the
    // page has shifted by the time Radix measures. We aggressively re-scroll
    // after close, retrying until the correct position sticks for ~1.5 s.
    let attempts = 0;
    const maxAttempts = 20;
    const poll = () => {
      attempts += 1;
      if (Math.abs(window.scrollY - y) > 5) {
        window.scrollTo({ top: y, left: 0, behavior: "instant" });
      }
      if (attempts < maxAttempts) setTimeout(poll, 80);
    };
    poll();
  };
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("proiecte-view") || "cards"); // "cards" | "list"
  const [finalizingProject, setFinalizingProject] = useState(null);  // null | project row
  const [finalizingBusy, setFinalizingBusy] = useState(false);
  const [dismantleDetail, setDismantleDetail] = useState(null); // {persons_planned, persons_actual, cars_*, dates, hours...}
  const [dismantleConfirm, setDismantleConfirm] = useState(null); // { project, persons, cars }
  const [dismantleBusy, setDismantleBusy] = useState(false);

  // Auto-scroll + highlight când vine cu ?project=ID sau ?regie=ID din alt modul
  // Dacă URL conține și &edit=1, deschide automat dialogul de editare
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get("project");
    const regieId = params.get("regie");
    const target = projectId || regieId;
    const shouldEdit = params.get("edit") === "1";
    if (!target) return;
    if (rows.length === 0 && regii.length === 0) return;
    // Show all categories/status to be sure target is visible
    setFilterCat("all");
    setStatusFilter("all");
    setHighlightId(target);
    // Auto-open edit dialog if requested
    if (shouldEdit) {
      if (projectId) {
        const proj = rows.find((p) => p.id === projectId);
        if (proj) edit(proj);
      } else if (regieId) {
        const reg = regii.find((r) => r.id === regieId);
        if (reg) editRegie(reg);
      }
    }
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="project-card-${target}"]`) || document.querySelector(`[data-testid="regie-card-${target}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
    const t = setTimeout(() => {
      setHighlightId(null);
      // Clean URL
      window.history.replaceState(null, "", "/proiecte");
    }, 2500);
    return () => clearTimeout(t);
  }, [rows.length, regii.length]);


  // Regie dialog state
  const [regieOpen, setRegieOpen] = useState(false);
  const [regieForm, setRegieForm] = useState(EMPTY_REGIE);
  const [regieEditing, setRegieEditing] = useState(null);
  // ===== Auto-save draft pentru regie nouă =====
  const REGIE_DRAFT_KEY = "convic.regie.new.draft.v1";
  const [regieDraftSavedAt, setRegieDraftSavedAt] = useState(null);
  useEffect(() => {
    if (!regieOpen || regieEditing) return;
    try {
      const raw = localStorage.getItem(REGIE_DRAFT_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      const hasContent = Object.values(data || {}).some((v) => typeof v === "string" ? v.trim() : Array.isArray(v) ? v.length : (typeof v === "number" ? v !== 0 : !!v));
      if (hasContent) {
        setRegieForm({ ...EMPTY_REGIE, ...data });
        setRegieDraftSavedAt(new Date());
        toast("📝 Am restaurat ciorna regiei nesalvate", { duration: 2500 });
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regieOpen, regieEditing]);
  useEffect(() => {
    if (!regieOpen || regieEditing) return;
    const t = setTimeout(() => {
      try {
        const hasContent = Object.entries(regieForm).some(([k, v]) => {
          if (k === "active") return false; // bool default true — nu îl considerăm conținut
          if (typeof v === "string") return v.trim().length > 0;
          if (Array.isArray(v)) return v.length > 0;
          if (typeof v === "number") return v > 0;
          return !!v && v !== EMPTY_REGIE[k];
        });
        if (hasContent) {
          localStorage.setItem(REGIE_DRAFT_KEY, JSON.stringify(regieForm));
          setRegieDraftSavedAt(new Date());
        }
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [regieForm, regieOpen, regieEditing]);
  const clearRegieDraft = () => {
    try { localStorage.removeItem(REGIE_DRAFT_KEY); } catch { /* ignore */ }
    setRegieDraftSavedAt(null);
  };

  const [siteCounts, setSiteCounts] = useState({}); // {project_id: {on_site, picked_up, total_active}}
  const navigate = useNavigate();

  const load = async () => {
    const [p, r, dd, sc] = await Promise.all([
      api.get("/projects"),
      api.get("/regii"),
      api.get("/projects/duplicate-dismissals").catch(() => ({ data: [] })),
      api.get("/inventory/site-leftovers/counts").catch(() => ({ data: {} })),
    ]);
    setRows(p.data);
    setRegii(r.data || []);
    setDuplicateDismissals(dd.data || []);
    setSiteCounts(sc.data || {});
  };
  useEffect(() => { load(); }, []);
  useSyncWatch(["projects", "regii"], () => { load(); });

  const submit = async () => {
    try {
      // Verificare dată start
      if (form.start_date && !form.indeterminate_start) {
        const sd = new Date(form.start_date + "T00:00:00");
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffDays = Math.floor((sd - today) / (1000 * 60 * 60 * 24));
        // Blocaj: data de start sub -365 zile (peste 1 an în trecut)
        if (diffDays < -365) {
          const yearsBack = Math.abs(Math.floor(diffDays / 365));
          toast.error(`Data de start ${fmtRoDate(form.start_date)} este cu ${Math.abs(diffDays)} zile (~${yearsBack} ${yearsBack === 1 ? "an" : "ani"}) în trecut. Modifică data te rog — nu poți crea șantier cu start atât de îndepărtat.`, { duration: 6000 });
          return;
        }
        // Avertisment: data de start peste 30 zile în viitor
        if (diffDays > 30) {
          const ok = await confirm({
            title: "Verifică data de început",
            body: `Ai setat data de start la ${fmtRoDate(form.start_date)} — peste ${diffDays} zile (${Math.round(diffDays / 30)} luni).\n\nEști sigur(ă) că șantierul începe în luna pe care ai introdus-o? Te rog verifică data și confirmă.`,
            confirmText: "DA, e corect",
            cancelText: "Verific din nou",
            icon: "📅",
          });
          if (!ok) return;
        }
      }
      // Calculează automat requires_post_measurement pe baza zonelor + alegerii Rückbau:
      //   - EKAS NON-Rückbau → True (mereu necesar)
      //   - EKAS Rückbau → folosește valoarea aleasă explicit (form.requires_post_measurement); fallback false
      //   - Factsheet → folosește valoarea aleasă (true/false)
      //   - Fără EKAS și fără Factsheet → null (irelevant)
      // Detectare zone: prefer quick_scope dacă e completat, altfel scope_items
      const zonesArr = Array.isArray(form.zones) ? form.zones : [];
      const qs = form.quick_scope || null;
      const quickEkasCount = qs && (qs.ekas_zones || 0) > 0 ? Number(qs.ekas_zones) : 0;
      const hasEkas = quickEkasCount > 0 || zonesArr.some((z) => (z?.type || "").toUpperCase() === "EKAS");
      const hasFactsheet = zonesArr.some((z) => (z?.type || "").toUpperCase() === "FACTSHEET");
      let computedReqMeas = null;
      if (hasEkas && !form.is_ruckbau) {
        computedReqMeas = true;
      } else if ((hasEkas && form.is_ruckbau) || hasFactsheet) {
        computedReqMeas = !!form.requires_post_measurement;
      } else {
        computedReqMeas = null;
      }
      // Strip UI-only fields (ex: _scope_mode) din payload
      const { _scope_mode: _sm, ...formCleaned } = form;
      const payload = {
        ...formCleaned,
        start_date: form.indeterminate_start ? "" : form.start_date,
        min_people: parseInt(form.min_people || 0) || 0,
        max_people: parseInt(form.min_people || 0) || 0,
        is_ruckbau: hasEkas ? !!form.is_ruckbau : false,
        requires_post_measurement: computedReqMeas,
      };
      if (editing) {
        const res = await api.put(`/projects/${editing.id}`, payload);
        const trimmed = res?.data?.schedule_entries_trimmed;
        toast.success(trimmed ? `Actualizat · ${trimmed} alocări din Planificator ajustate la noua dată de sfârșit` : "Actualizat");
      } else {
        await api.post("/projects", payload);
        toast.success("Proiect adăugat");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY_PROJ);
      clearDraft();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const edit = (r) => {
    setEditing(r);
    // Dacă e proiect demontat → încarcă detaliile demontajului
    setDismantleDetail(null);
    if (r && r.id && (String(r.category) === "7" || r.status === "Demontat" || r.dismantle_date)) {
      api.get(`/projects/${r.id}/dismantle-detail`)
        .then((res) => setDismantleDetail(res.data || null))
        .catch(() => setDismantleDetail(null));
    }
    setForm({
      name: r.name || "", category: r.category || "1", status: r.status || "În lucru",
      start_date: r.start_date || "", end_date: r.end_date || "",
      indeterminate_start: !!r.indeterminate_start,
      auftraggeber: r.auftraggeber || "", auftraggeber_phone: r.auftraggeber_phone || "", auftraggeber_contact: r.auftraggeber_contact || "",
      bauherr: r.bauherr || "", bauherr_phone: r.bauherr_phone || "",
      bauleiter: r.bauleiter || "", bauleiter_phone: r.bauleiter_phone || "",
      adresa: r.adresa || "",
      scope_items: Array.isArray(r.scope_items) ? r.scope_items : [],
      quick_scope: r.quick_scope || null,
      // Auto-detectează modul: rapid dacă quick_scope are date, altfel detaliat dacă scope_items are date, altfel rapid.
      _scope_mode: (r.quick_scope && ((r.quick_scope.ekas_zones || 0) > 0 || (r.quick_scope.materials || []).length > 0))
        ? "rapid"
        : (Array.isArray(r.scope_items) && r.scope_items.length > 0 ? "detaliat" : "rapid"),
      is_renovation: typeof r.is_renovation === "boolean" ? r.is_renovation : true,
      min_people: r.min_people || "",
      max_people: r.max_people || "",
      zones: Array.isArray(r.zones) ? r.zones : [],
      special: r.special || "",
      utilities: r.utilities || null,
      contact_email: r.contact_email || "",
      vorarbeiter: r.vorarbeiter || "",
      description: r.description || "",
      oferta_deadline: r.oferta_deadline || "",
      color: r.color || "",
      is_ruckbau: !!r.is_ruckbau,
      requires_post_measurement: typeof r.requires_post_measurement === "boolean" ? r.requires_post_measurement : null,
    });
    setOpen(true);
  };

  const remove = async (id) => { if (window.confirm("Șterge?")) { await api.delete(`/projects/${id}`); load(); } };

  // Admin shortcut: mark project as "Finalizat – De demontat" (without photo flow)
  const markFinalizat = (r) => {
    setFinalizingProject(r);
  };

  const confirmMarkFinalizat = async () => {
    if (!finalizingProject) return;
    setFinalizingBusy(true);
    try {
      await api.patch(`/projects/${finalizingProject.id}/status`, { status: "Finalizat – De demontat" });
      toast.success(`"${finalizingProject.name}" trecut la De demontat`);
      setFinalizingProject(null);
      // Comută filtrul la "De demontat" automat ca utilizatorul să vadă proiectul în noua categorie
      setStatusFilter("de_demontat");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally {
      setFinalizingBusy(false);
    }
  };

  // Send a "De demontat" project back to "În lucru"
  const reopenProject = async (r) => {
    if (!(await confirm({ body: `Trimiți „${r.name}" înapoi la "În lucru"?` }))) return;
    try {
      await api.patch(`/projects/${r.id}/status`, { status: "În lucru" });
      toast.success(`"${r.name}" mutat înapoi la În lucru`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    }
  };

  // Move a "De demontat" project (cat 6.1) → "Demontat de Facturat" (cat 7)
  // Adminii nu demontează ei înșiși — deschidem un dialog pentru a selecta echipa reală.
  const markDemontat = async (r) => {
    try {
      const [persRes, carsRes] = await Promise.all([
        api.get("/persons", { params: { active_only: 1 } }),
        api.get("/cars"),
      ]);
      const persons = (persRes.data || []).map((p) => ({
        id: p.id,
        name: `${p.nume || ""} ${p.prenume || ""}`.trim() || (p.username || "—"),
        is_admin_user: p.is_admin_user || false,
      }));
      const cars = (carsRes.data || [])
        .filter((c) => !c.is_equipment && c.status !== "sold")
        .map((c) => ({
          id: c.id,
          marca: c.marca,
          model: c.model,
          culoare: c.culoare,
          numar_inmatriculare: c.numar_inmatriculare,
          intern_number: c.intern_number,
          label: `${c.marca || ""} ${c.model || ""} ${c.numar_inmatriculare ? `(${c.numar_inmatriculare})` : ""}`.trim() || c.id,
        }));
      setDismantleConfirm({ project: r, persons, cars });
    } catch (e) {
      toast.error("Eroare la încărcarea persoanelor / mașinilor");
    }
  };

  const submitDismantle = async ({ personIds, carIds, note, dismantleDate, brought_back, left_on_site }) => {
    if (!dismantleConfirm) return;
    if (!personIds || personIds.length === 0) {
      toast.error("Selectează cel puțin o persoană care a demontat");
      return;
    }
    setDismantleBusy(true);
    try {
      // 1) Procesează echipamentele de pe șantier (dacă există)
      const bb = Array.isArray(brought_back) ? brought_back : [];
      const los = Array.isArray(left_on_site) ? left_on_site : [];
      if (bb.length > 0 || los.length > 0) {
        await api.post("/inventory/site-leftovers/dismantle-batch", {
          project_id: dismantleConfirm.project.id,
          brought_back: bb,
          left_on_site: los,
          note: note || null,
        });
      }
      // 2) Apoi schimbă statusul proiectului
      await api.patch(`/projects/${dismantleConfirm.project.id}/status`, {
        status: "Demontat de Facturat",
        dismantled_by_person_ids: personIds,
        dismantled_by_car_ids: carIds || [],
        dismantled_note: note || "",
        dismantle_date: dismantleDate,
      });
      const parts = [];
      parts.push(`„${dismantleConfirm.project.name}" trecut la Demontat de Facturat`);
      if (bb.length) parts.push(`📦 ${bb.length} aduse înapoi`);
      if (los.length) parts.push(`🚧 ${los.length} rămase pe șantier`);
      toast.success(parts.join(" · "));
      setDismantleConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally {
      setDismantleBusy(false);
    }
  };

  const [docsFor, setDocsFor] = useState(null);
  const [hoursReportFor, setHoursReportFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [regieDetailFor, setRegieDetailFor] = useState(null);

  const submitRegie = async () => {
    if (!regieForm.name.trim()) { toast.error("Denumire regie obligatorie"); return; }
    if (!(regieForm.firma || "").trim()) { toast.error("Firma este obligatorie — selectează firma unde trimitem oamenii"); return; }
    // 🟢 Validare durată regie (fix bug: Tudor a salvat regii fără durată → nu apăreau în Planificator)
    if (!regieForm.start_date) { toast.error("Data de început este obligatorie"); return; }
    if (!regieForm.indeterminate_end && !regieForm.end_date) {
      toast.error("Setează data de sfârșit sau bifează Perioadă nedeterminată"); return;
    }
    if (!regieForm.indeterminate_end && regieForm.end_date && regieForm.end_date < regieForm.start_date) {
      toast.error("Data de sfârșit nu poate fi înainte de data de început"); return;
    }
    try {
      // Sanitize default_min_people_by_day: array de 7 (null sau int>=0)
      const byDay = Array.isArray(regieForm.default_min_people_by_day) ? regieForm.default_min_people_by_day : [];
      const sanitizedByDay = Array.from({ length: 7 }, (_, i) => {
        const v = byDay[i];
        if (v === null || v === undefined || v === "") return null;
        const n = parseInt(v);
        return Number.isFinite(n) && n >= 0 ? n : null;
      });
      const anyDayValueSet = sanitizedByDay.some((v) => v !== null);
      const payload = {
        ...regieForm,
        default_min_people: parseInt(regieForm.default_min_people) || 0,
        default_max_people: parseInt(regieForm.default_min_people) || 0,
        default_min_people_by_day: anyDayValueSet ? sanitizedByDay : null,
      };
      if (regieEditing) {
        await api.put(`/regii/${regieEditing.id}`, payload);
        toast.success("Regie actualizată");
        if (payload.active === false) {
          toast.warning("Atenție: regia este INACTIVĂ — nu va apărea în Planificator", { duration: 8000 });
        }
      } else {
        await api.post("/regii", payload);
        toast.success("Regie adăugată");
      }
      setRegieOpen(false);
      setRegieEditing(null);
      setRegieForm(EMPTY_REGIE);
      clearRegieDraft();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const editRegie = (r) => {
    setRegieEditing(r);
    setRegieForm({
      name: r.name || "", firma: r.firma || "", adresa: r.adresa || "",
      vorarbeiter: r.vorarbeiter || "",
      report_frequency: r.report_frequency || "saptamanal",
      color: r.color || REGIE_COLORS[0], active: r.active !== false, note: r.note || "",
      start_date: r.start_date || "", end_date: r.end_date || "",
      indeterminate_end: !!r.indeterminate_end,
      default_min_people: r.default_min_people || "",
      default_max_people: r.default_max_people || "",
      default_min_people_by_day: Array.isArray(r.default_min_people_by_day) && r.default_min_people_by_day.length === 7
        ? r.default_min_people_by_day
        : [null, null, null, null, null, null, null],
    });
    setRegieOpen(true);
  };

  const removeRegie = async (r) => {
    if (!window.confirm(`Șterge regia ${r.name}? Se șterg și pontajele săptămânale.`)) return;
    await api.delete(`/regii/${r.id}`);
    load();
  };

  const sq = search.trim().toLowerCase();
  const matchesProject = (p) => !sq || `${p.name || ""} ${p.description || ""} ${p.status || ""} ${p.adresa || ""}`.toLowerCase().includes(sq);
  const matchesRegie = (r) => !sq || `${r.name || ""} ${r.firma || ""} ${r.adresa || ""} ${r.note || ""}`.toLowerCase().includes(sq);

  // Detector de potențiale duplicate: proiecte ACTIVE (cat NOT IN 7,8,9,10) cu nume similare
  // (numele normalizat FĂRĂ cifre/punctuație trebuie să fie identic) ȘI perioadă suprapusă.
  // Astfel „AN-1069 Wetzikon" și „AN 1096 Wetzikon" se grupează (ambele → „an wetzikon"),
  // dar „Davos MABA" și „Berneck MABA" NU (au prefix diferit).
  const duplicateGroups = useMemo(() => {
    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const stripDigits = (name) => norm(name).replace(/[^a-zäöüß ]/g, " ").replace(/\s+/g, " ").trim();
    const overlapsRange = (a, b) => {
      const aS = a.start_date || "", aE = a.end_date || "9999-12-31";
      const bS = b.start_date || "", bE = b.end_date || "9999-12-31";
      if (!aS && !bS) return true;
      const s1 = aS || "0000-01-01", e1 = aE;
      const s2 = bS || "0000-01-01", e2 = bE;
      return s1 <= e2 && s2 <= e1;
    };
    const active = rows.filter((p) => !["7", "8", "9", "10"].includes(String(p.category || "")));
    const buckets = new Map();
    for (const p of active) {
      const k = stripDigits(p.name);
      if (!k || k.length < 3) continue;
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k).push(p);
    }
    const groups = [];
    for (const [key, list] of buckets.entries()) {
      if (list.length < 2) continue;
      // Verifică perioadă suprapusă pe cel puțin o pereche
      let hasOverlap = false;
      outer: for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
          if (overlapsRange(list[i], list[j])) { hasOverlap = true; break outer; }
        }
      }
      if (hasOverlap) {
        // Cheia de dismissal = ID-urile sortate ale tuturor proiectelor din grup
        const dismissalKey = list.map((x) => x.id).slice().sort().join("|");
        // Folosim numele primului proiect ca label vizibil (nu cheia normalizată)
        groups.push({ key, label: list[0].name, projects: list, dismissalKey });
      }
    }
    return groups;
  }, [rows]);

  // Set de chei dismissed (proiecte_ids sortate join "|")
  const dismissedKeys = useMemo(() => {
    const s = new Set();
    for (const d of duplicateDismissals) {
      const k = (d.project_ids || []).slice().sort().join("|");
      if (k) s.add(k);
    }
    return s;
  }, [duplicateDismissals]);

  const visibleDuplicateGroups = useMemo(
    () => duplicateGroups.filter((g) => !dismissedKeys.has(g.dismissalKey)),
    [duplicateGroups, dismissedKeys]
  );

  const dismissDuplicateGroup = async (group) => {
    if (!group) return;
    if (!(await confirm({
      title: "Confirmi că NU sunt duplicate?",
      body: `Marchezi grupul „${group.label}" (${group.projects.length} proiecte) ca verificat — banner-ul va dispărea. Dacă vrei să apară din nou, șterge unul dintre proiecte sau adaugă unul nou similar.`,
      confirmText: "DA, marchează ca OK",
      cancelText: "Anulează",
    }))) return;
    try {
      const ids = group.projects.map((p) => p.id);
      const res = await api.post("/projects/duplicate-dismissals", { project_ids: ids });
      setDuplicateDismissals((prev) => [...prev.filter((d) => d.id !== res.data.id), res.data]);
      setDuplicateModalKey(null);
      toast.success(`Grup „${group.label}" marcat ca OK`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la confirmare");
    }
  };

  const [duplicateModalKey, setDuplicateModalKey] = useState(null);

  // 🩺 Diagnostic vizibilitate regie în Planificator
  const [diagRegie, setDiagRegie] = useState(null);
  const [diagDate, setDiagDate] = useState("");
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const nextMondayIso = () => {
    const d = new Date();
    d.setDate(d.getDate() + (((8 - d.getDay()) % 7) || 7));
    return d.toISOString().slice(0, 10);
  };
  const runDiag = async (rid, day) => {
    setDiagLoading(true);
    try {
      const { data } = await api.get(`/regii/${rid}/diagnose`, { params: { day } });
      setDiagResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la diagnostic"); }
    setDiagLoading(false);
  };
  const openDiag = (r) => {
    const day = nextMondayIso();
    setDiagRegie(r); setDiagDate(day); setDiagResult(null);
    runDiag(r.id, day);
  };

  // Filtru pe status: in_lucru | de_demontat | demontat | toate
  // "În lucru" = orice status ce NU este "Finalizat – De demontat" sau "Demontat"

  // Planificator: vede DOAR categoriile 6, 6.1, 7 (Sanierungskonzept → De demontat → Demontat de Facturat)
  const PLANIFICATOR_VISIBLE_CATS = ["6", "6.1", "7"];
  const matchesRoleScope = (p) =>
    !isPlanificator || PLANIFICATOR_VISIBLE_CATS.includes(String(p.category || ""));

  // Planificator: vede regiile active SAU cu date viitoare (start_date >= azi)
  const todayIso = new Date().toISOString().slice(0, 10);
  const matchesRoleScopeRegie = (r) => {
    if (!isPlanificator) return true;
    if (r.active !== false) return true;
    return r.start_date && r.start_date >= todayIso;
  };

  const filteredRegii = regii.filter(matchesRegie).filter(matchesRoleScopeRegie);
  const allFilteredRows = rows.filter(matchesProject).filter(matchesRoleScope);
  // Filtrare pe status: click pe pastilele de status → arată doar proiectele din acel bucket
  const filteredRows = statusFilter === "all"
    ? allFilteredRows
    : statusFilter === "pe_rol"
      ? allFilteredRows.filter((p) => PE_ROL_CATS.includes(String(p.category || "")))
      : allFilteredRows.filter((p) => statusBucket(p) === statusFilter);

  const counts = useMemo(() => {
    const c = { in_lucru: 0, de_demontat: 0, demontat: 0, all: allFilteredRows.length };
    for (const p of allFilteredRows) c[statusBucket(p)]++;
    c.pe_rol = allFilteredRows.filter((p) => PE_ROL_CATS.includes(String(p.category || ""))).length;
    return c;
  }, [allFilteredRows]);

  const grouped = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    // Calculează "urgency rank": cu cât mai mic, cu atât mai sus pe listă
    // 0 = În lucru (start trecut sau azi, NU finalizat/demontat)
    // 1+N = Neîncepute, sortate cronologic crescător după start_date
    // 999 = Fără start_date (jos)
    // 9999 = Demontate (ultimele)
    const rank = (p) => {
      const sb = statusBucket(p);
      if (sb === "demontat") return 9999;
      if (sb === "de_demontat") return 1; // urgent — chiar dacă nu mai e "în lucru"
      const sd = p.start_date;
      if (!sd) return 999;
      if (sd <= today) return 0; // în execuție acum
      // În viitor — ordonate cronologic
      return 100 + Math.min(1000, Math.ceil((new Date(sd).setHours(0,0,0,0) - new Date(today).setHours(0,0,0,0)) / 86400000));
    };
    const map = {};
    for (const r of filteredRows) {
      const k = r.category || "1";
      (map[k] = map[k] || []).push(r);
    }
    // Sortează fiecare bucket după urgency
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => {
        const ra = rank(a);
        const rb = rank(b);
        if (ra !== rb) return ra - rb;
        // Tie-breaker: alphabetic by name
        return (a.name || "").localeCompare(b.name || "", "ro");
      });
    }
    return map;
  }, [filteredRows]);

  const visibleCategories = filterCat === "all"
    ? PROJECT_CATEGORIES.filter((c) => (grouped[c.code] || []).length > 0)
    : PROJECT_CATEGORIES.filter((c) => c.code === filterCat);

  return (
    <div data-testid="page-proiecte">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold">{sectionTab === "regii" ? "⚙ Regii" : sectionTab === "projects" ? "🏗 Proiecte" : "Proiecte și Regii"}</h1>
          <p className="text-sm text-zinc-500 mt-1">13 categorii · fluxul complet de la ofertă la facturare</p>
        </div>
        {canDo("PROIECTE", "add") && (
          <div className="flex gap-2">
            <Dialog open={!!diagRegie} onOpenChange={(v) => { if (!v) { setDiagRegie(null); setDiagResult(null); } }}>
              <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="regie-diag-dialog">
                <DialogHeader>
                  <DialogTitle>🩺 Diagnostic Planificator — {diagRegie?.name}</DialogTitle>
                </DialogHeader>
                <div className="flex items-end gap-3">
                  <div>
                    <Label className="small-label">Verifică pentru ziua</Label>
                    <Input type="date" value={diagDate} className="mt-1 w-44" data-testid="regie-diag-date"
                      onChange={(e) => { setDiagDate(e.target.value); if (e.target.value && diagRegie) runDiag(diagRegie.id, e.target.value); }} />
                  </div>
                  {diagLoading && <span className="text-xs text-zinc-400 italic pb-2">Se verifică…</span>}
                </div>
                {diagResult && (
                  <>
                    <div className={`rounded-lg border-2 p-3 text-sm font-medium ${diagResult.verdict.startsWith("✅") ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-700"}`} data-testid="regie-diag-verdict">
                      {diagResult.verdict}
                    </div>
                    <div className="space-y-1.5">
                      {diagResult.checks.map((c, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs border-b border-zinc-100 pb-1.5">
                          <span className={c.ok ? "text-emerald-600" : "text-red-600"}>{c.ok ? "✓" : "✗"}</span>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold">{c.check}</div>
                            <div className="text-zinc-500">{c.detail}</div>
                            {(c.duplicates || []).map((dp) => (
                              <div key={dp.id} className="text-[10px] text-amber-700 mt-0.5">↳ duplicat: {dp.name} ({dp.firma || "—"}) · {dp.active === false ? "INACTIV" : "activ"} · {dp.indeterminate_end ? "nedeterminat" : (dp.end_date || "fără sfârșit")}</div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
            <Button asChild variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="open-istoric-demontari">
              <Link to="/istoric-demontari">
                <History size={16} className="mr-1.5"/> Istoric demontări
              </Link>
            </Button>
            <Dialog open={regieOpen} onOpenChange={(v) => {
              if (!v && !regieEditing) {
                setRegieOpen(false);
                restoreScroll();
                return;
              }
              setRegieOpen(v);
              if (!v) { setRegieForm(EMPTY_REGIE); setRegieEditing(null); restoreScroll(); }
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-regie-btn" className="border-[var(--orange-bila)] text-[var(--orange-bila)] hover:bg-[var(--orange-bila-bg)]">
                  <Briefcase size={16}/> Adaugă regie
                </Button>
              </DialogTrigger>
              <DialogContent
                className="max-w-xl max-h-[90vh] overflow-y-auto"
                onInteractOutside={(e) => { if (!regieEditing) e.preventDefault(); }}
                onEscapeKeyDown={(e) => { if (!regieEditing) e.preventDefault(); }}
              >
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {regieEditing ? "Editează regie" : "Regie nouă"}
                    {!regieEditing && regieDraftSavedAt && (
                      <span
                        className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"
                        title={`Ciornă auto-salvată local la ${regieDraftSavedAt.toLocaleTimeString("ro-RO")}`}
                        data-testid="regie-draft-saved"
                      >
                        💾 Auto-salvat
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2"><Label>Denumire regie</Label><Input data-testid="regie-name" value={regieForm.name} onChange={(e) => setRegieForm({ ...regieForm, name: e.target.value })} placeholder="ex: Regie ACME GmbH" /></div>
                  <div className="md:col-span-2">
                    <Label>Firmă (la cine trimitem oamenii) <span className="text-red-600">*</span></Label>
                    {(() => {
                      // Adună firmele cunoscute: lista predefinită + cele existente în regii
                      const DEFAULT_FIRMS = ["Implenia", "Speztec", "Klarotec", "Maba", "Imas"];
                      const fromRegii = Array.from(new Set((regii || []).map((r) => (r.firma || "").trim()).filter(Boolean)));
                      const merged = Array.from(new Set([...DEFAULT_FIRMS, ...fromRegii])).sort((a, b) => {
                        const pri = (n) => {
                          const i = DEFAULT_FIRMS.findIndex((d) => d.toLowerCase() === n.toLowerCase());
                          return i === -1 ? 99 : i;
                        };
                        const dp = pri(a) - pri(b);
                        return dp !== 0 ? dp : a.localeCompare(b);
                      });
                      const currentLower = (regieForm.firma || "").trim().toLowerCase();
                      const isInList = merged.some((f) => f.toLowerCase() === currentLower);
                      const selValue = !regieForm.firma ? "__none__" : (isInList ? merged.find((f) => f.toLowerCase() === currentLower) : "__custom__");
                      return (
                        <>
                          <Select
                            value={selValue}
                            onValueChange={(v) => {
                              if (v === "__add__") {
                                const name = window.prompt("Nume firmă nouă:");
                                if (!name) return;
                                setRegieForm({ ...regieForm, firma: name.trim() });
                                return;
                              }
                              if (v === "__none__") { setRegieForm({ ...regieForm, firma: "" }); return; }
                              if (v === "__custom__") return; // păstrează textul liber existent
                              setRegieForm({ ...regieForm, firma: v });
                            }}
                          >
                            <SelectTrigger className="mt-1" data-testid="regie-firma-select"><SelectValue placeholder="Selectează firma"/></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Selectează —</SelectItem>
                              {merged.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                              {!isInList && regieForm.firma && <SelectItem value="__custom__">📝 {regieForm.firma} (custom)</SelectItem>}
                              <SelectItem value="__add__">➕ Adaugă firmă nouă…</SelectItem>
                            </SelectContent>
                          </Select>
                          {!isInList && regieForm.firma && (
                            <div className="mt-1.5 text-[11px] text-amber-700">
                              ⚠ „{regieForm.firma}" nu e în lista standard. Selectează una existentă sau lasă custom.
                            </div>
                          )}
                          {/* Hidden input pentru testing - pastreaza data-testid pentru compatibilitate */}
                          <input type="hidden" data-testid="regie-firma" value={regieForm.firma || ""} readOnly/>
                        </>
                      );
                    })()}
                  </div>
                  <div className="md:col-span-2"><Label>Adresă proiect</Label><Input data-testid="regie-adresa" value={regieForm.adresa} onChange={(e) => setRegieForm({ ...regieForm, adresa: e.target.value })} /></div>
                  <div className="md:col-span-2">
                    <Label>👷 Vorarbeiter / Șef echipă</Label>
                    <Input
                      data-testid="regie-vorarbeiter"
                      value={regieForm.vorarbeiter}
                      onChange={(e) => setRegieForm({ ...regieForm, vorarbeiter: e.target.value })}
                      placeholder="ex: Hans Müller · +41 79 123 45 67"
                    />
                    <p className="text-[11px] text-zinc-500 mt-1">Numele șefului de echipă de la această regie/firmă — apare pe cardul regiei în Planificator.</p>
                  </div>
                  <div className="md:col-span-2">
                    <Label>📋 Frecvența raportului de ore</Label>
                    <div className="flex gap-2 mt-1">
                      {[{ v: "saptamanal", l: "📅 Săptămânal (ex: Implenia)" }, { v: "zilnic", l: "📆 Zilnic (ex: Imas)" }].map((o) => (
                        <button
                          key={o.v}
                          type="button"
                          onClick={() => setRegieForm({ ...regieForm, report_frequency: o.v })}
                          className={`text-xs px-3 py-2 rounded-md border font-semibold transition-colors ${(regieForm.report_frequency || "saptamanal") === o.v ? "bg-[var(--primary)] text-white border-[var(--primary)]" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}
                          data-testid={`regie-report-freq-${o.v}`}
                        >
                          {o.l}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-1">Cât de des face firma raport de ore — zilnic sau o dată pe săptămână.</p>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Culoare regie <span className="text-zinc-500 font-normal">— alege o nuanță (grupate pe familii)</span></Label>
                    <p className="text-[11px] text-zinc-500 mb-2 mt-1">
                      Culoarea distinge regia vizual în Planificator. <strong>Culorile setului de scule (🔴🟡🔵🟢⚪🟣) NU sunt disponibile aici</strong> — sunt rezervate pentru alocarea echipamentelor.
                    </p>
                    <div className="mt-1 space-y-1 max-h-[260px] overflow-y-auto pr-1 border border-zinc-200 rounded p-2 bg-zinc-50">
                      {Object.entries(REGIE_COLOR_FAMILIES).map(([family, shades]) => (
                        <div key={family} className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-600 w-[80px] flex-shrink-0">{family}</span>
                          <div className="flex flex-wrap gap-1">
                            {shades.map((c) => (
                              <button
                                key={c}
                                type="button"
                                onClick={() => setRegieForm({ ...regieForm, color: c })}
                                className={`w-6 h-6 rounded border-2 transition-all ${regieForm.color === c ? "border-zinc-900 scale-110 ring-2 ring-offset-1 ring-zinc-400" : "border-white hover:scale-110 hover:border-zinc-300"}`}
                                style={{ background: c }}
                                data-testid={`regie-color-${c}`}
                                title={`${family} ${c}`}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-1">
                      Culoarea aleasă: <span className="inline-block w-3 h-3 rounded align-middle border border-zinc-300" style={{ background: regieForm.color }}/> <code className="font-mono">{regieForm.color}</code>
                    </div>
                  </div>
                  <div className="md:col-span-2 flex items-center gap-3">
                    <Label className="small-label">Activă</Label>
                    <Switch data-testid="regie-active-toggle" checked={regieForm.active !== false} onCheckedChange={(v) => setRegieForm({ ...regieForm, active: v })} />
                    <span className="text-xs text-zinc-500">Regiile inactive nu apar în planul lunar</span>
                  </div>
                  {regieForm.active === false && (
                    <div className="md:col-span-2 border-2 border-red-300 bg-red-50 rounded-lg p-3 text-sm text-red-700 font-medium" data-testid="regie-inactive-warning">
                      ⚠️ Această regie este <strong>INACTIVĂ</strong> — NU va apărea în Planificator și nu vei putea aloca oameni pe ea, chiar dacă perioada e nedeterminată sau în viitor. Pornește switch-ul „Activă" de mai sus pentru a o replanifica.
                    </div>
                  )}
                  <div className="md:col-span-2"><Label>Notă</Label><Textarea rows={2} value={regieForm.note} onChange={(e) => setRegieForm({ ...regieForm, note: e.target.value })} /></div>

                  <div className="md:col-span-2 border-t pt-3 mt-1">
                    <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">⏱ Interval & necesar</div>
                  </div>
                  <div>
                    <Label>Data început</Label>
                    <Input type="date" data-testid="regie-start-date" value={regieForm.start_date} onChange={(e) => setRegieForm({ ...regieForm, start_date: e.target.value })}/>
                  </div>
                  <div>
                    <Label>Data sfârșit</Label>
                    <Input type="date" data-testid="regie-end-date" value={regieForm.end_date} disabled={regieForm.indeterminate_end} onChange={(e) => {
                      const v = e.target.value;
                      const next = { ...regieForm, end_date: v };
                      if (v && v >= new Date().toISOString().slice(0, 10) && regieForm.active === false) {
                        next.active = true;
                        toast.info("Regia a fost reactivată automat (era inactivă) — va reapărea în Planificator");
                      }
                      setRegieForm(next);
                    }}/>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Switch
                        data-testid="regie-indeterminate"
                        checked={!!regieForm.indeterminate_end}
                        onCheckedChange={(v) => {
                          const next = { ...regieForm, indeterminate_end: v, end_date: v ? "" : regieForm.end_date };
                          if (v && regieForm.active === false) {
                            next.active = true;
                            toast.info("Regia a fost reactivată automat (era inactivă) — va reapărea în Planificator");
                          }
                          setRegieForm(next);
                        }}
                      />
                      <Label className="small-label">Nedeterminat</Label>
                    </div>
                  </div>
                  {/* Toggle Săptămânal vs Zilnic + câmpuri minim personal */}
                  <div className="md:col-span-2 border-2 border-amber-200 bg-amber-50/50 rounded-lg p-3">
                    {(() => {
                      const dByDay = regieForm.default_min_people_by_day || [];
                      const dailyMode = dByDay.some((v) => v !== null && v !== "" && v !== undefined);
                      return (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="small-label text-amber-800 font-bold">👥 Minim necesar personal</Label>
                            <div className="flex rounded-md border border-amber-300 overflow-hidden text-[10px] font-bold uppercase tracking-wider">
                              <button
                                type="button"
                                onClick={() => setRegieForm({ ...regieForm, default_min_people_by_day: [null, null, null, null, null, null, null] })}
                                className={`px-2 py-1 transition-colors ${!dailyMode ? "bg-amber-600 text-white" : "bg-white text-amber-700 hover:bg-amber-100"}`}
                                data-testid="regie-min-mode-weekly"
                              >
                                📅 Săptămânal
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const base = parseInt(regieForm.default_min_people) || 0;
                                  // Activează modul zilnic completând zilele lucrătoare cu valoarea săptămânală
                                  setRegieForm({ ...regieForm, default_min_people_by_day: [base, base, base, base, base, 0, 0] });
                                }}
                                className={`px-2 py-1 transition-colors ${dailyMode ? "bg-amber-600 text-white" : "bg-white text-amber-700 hover:bg-amber-100"}`}
                                data-testid="regie-min-mode-daily"
                              >
                                📊 Zilnic
                              </button>
                            </div>
                          </div>
                          {!dailyMode ? (
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="small-label">Minim personal (Lu-Vi)</Label>
                                <Input type="number" min={0} placeholder="ex: 3" data-testid="regie-min-people" value={regieForm.default_min_people} onChange={(e) => setRegieForm({ ...regieForm, default_min_people: e.target.value })}/>
                              </div>
                            </div>
                          ) : (
                            <div>
                              {(() => {
                                const base = parseInt(regieForm.default_min_people) || 0;
                                const setByDay = (next) => setRegieForm({ ...regieForm, default_min_people_by_day: next });
                                const presets = [
                                  { label: "Lu–Vi plin", value: [base, base, base, base, base, 0, 0], testid: "preset-mon-fri" },
                                  { label: "Lu–Jo plin", value: [base, base, base, base, 0, 0, 0], testid: "preset-mon-thu" },
                                  { label: "Toată săpt.", value: [base, base, base, base, base, base, base], testid: "preset-mon-sun" },
                                  { label: "Resetează", value: [null, null, null, null, null, null, null], testid: "preset-reset" },
                                ];
                                return (
                                  <div className="flex items-center flex-wrap gap-1 mb-2">
                                    <span className="text-[9px] uppercase tracking-wider font-bold text-amber-700">Preset rapid:</span>
                                    {presets.map((p) => (
                                      <button
                                        key={p.testid}
                                        type="button"
                                        onClick={() => setByDay(p.value)}
                                        className="text-[10px] px-2 py-0.5 rounded border border-amber-300 bg-white text-amber-700 hover:bg-amber-100 transition-colors"
                                        data-testid={`regie-${p.testid}`}
                                        title={p.testid === "preset-reset" ? "Toate zilele revin la fallback săptămânal" : `Aplică pattern (val. de bază: ${base})`}
                                      >
                                        {p.label}
                                      </button>
                                    ))}
                                  </div>
                                );
                              })()}
                              <div className="grid grid-cols-7 gap-1.5" data-testid="regie-min-by-day-grid">
                                {["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"].map((d, i) => (
                                  <div key={d} className="flex flex-col items-center">
                                    <span className="text-[10px] font-bold text-amber-700 mb-0.5">{d}</span>
                                    <Input
                                      type="number"
                                      min={0}
                                      placeholder="—"
                                      value={dByDay[i] ?? ""}
                                      onChange={(e) => {
                                        const next = [...(regieForm.default_min_people_by_day || [null, null, null, null, null, null, null])];
                                        const raw = e.target.value;
                                        next[i] = raw === "" ? null : Math.max(0, parseInt(raw) || 0);
                                        setRegieForm({ ...regieForm, default_min_people_by_day: next });
                                      }}
                                      className="h-9 text-center text-sm font-bold tabular-nums px-1"
                                      data-testid={`regie-min-day-${i}`}
                                    />
                                  </div>
                                ))}
                              </div>
                              <div className="text-[10px] text-zinc-600 mt-1.5">
                                💡 Lasă o zi goală pentru a folosi minimul săptămânal implicit ({parseInt(regieForm.default_min_people) || 0} pers).
                              </div>
                              <div className="grid grid-cols-2 gap-2 mt-2">
                                <div>
                                  <Label className="small-label">Min săptămânal (fallback)</Label>
                                  <Input type="number" min={0} placeholder="ex: 3" data-testid="regie-min-people" value={regieForm.default_min_people} onChange={(e) => setRegieForm({ ...regieForm, default_min_people: e.target.value })}/>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  <div className="md:col-span-2 text-[11px] text-zinc-500">
                    💡 Necesarul se aplică zilnic pe toată durata regiei. La salvare, intervalul ales se creează automat în calendarul Management. Dacă alegi „Nedeterminat", se creează 12 săptămâni inițial.
                  </div>
                </div>
                <DialogFooter className="gap-2 flex-wrap">
                  {!regieEditing && regieDraftSavedAt && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!window.confirm("Renunți la ciornă? Datele introduse până acum vor fi șterse.")) return;
                        clearRegieDraft();
                        setRegieForm(EMPTY_REGIE);
                        setRegieOpen(false);
                        toast.success("Ciornă ștearsă");
                      }}
                      className="text-red-600 hover:bg-red-50 border-red-200"
                      data-testid="regie-discard-draft"
                    >
                      🗑 Renunță la ciornă
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (!regieEditing && regieDraftSavedAt) {
                        setRegieOpen(false);
                        toast("📝 Ciorna a fost păstrată — o vei găsi la următoarea deschidere", { duration: 2500 });
                        return;
                      }
                      setRegieOpen(false);
                      setRegieEditing(null);
                      setRegieForm(EMPTY_REGIE);
                    }}
                    data-testid="regie-cancel"
                  >
                    {(!regieEditing && regieDraftSavedAt) ? "Închide (păstrează ciorna)" : "Anulează"}
                  </Button>
                  <Button onClick={submitRegie} data-testid="submit-regie" className="bg-[var(--orange-bila)] hover:opacity-90">Salvează</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={(v) => {
              if (!v && !editing) {
                // Proiect nou: NU închidem accidental — păstrăm draft-ul auto-salvat și forțăm Anulează/Salvează
                // Permit însă închiderea dacă utilizatorul a apăsat Esc/X (decis în butoanele explicite)
                setOpen(false);
                restoreScroll();
                return;
              }
              setOpen(v);
              if (!v) { setForm(EMPTY_PROJ); setEditing(null); restoreScroll(); }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-project-btn" className="bg-[var(--primary)]"><Plus size={16}/> Proiect nou</Button>
              </DialogTrigger>
              <DialogContent
                className="max-w-3xl max-h-[90vh] overflow-y-auto"
                onInteractOutside={(e) => { if (!editing) e.preventDefault(); }}
                onEscapeKeyDown={(e) => { if (!editing) e.preventDefault(); }}
              >
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {editing ? "Editează" : "Proiect nou"}
                    {!editing && draftSavedAt && (
                      <span
                        className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"
                        title={`Ciornă auto-salvată local la ${draftSavedAt.toLocaleTimeString("ro-RO")}`}
                        data-testid="proj-draft-saved"
                      >
                        💾 Auto-salvat
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>
                {/* === Detalii Demontaj (doar pentru proiecte demontate) === */}
                {dismantleDetail && dismantleDetail.is_dismantled && (
                  <div className="mb-3 rounded-lg border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-orange-50/50 p-3" data-testid="dismantle-detail-block">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] uppercase tracking-wider font-extrabold bg-emerald-600 text-white px-2 py-0.5 rounded">✅ DEMONTAT</span>
                      {dismantleDetail.dismantle_date && (
                        <span className="text-sm font-bold text-emerald-800">
                          {new Date(dismantleDetail.dismantle_date + "T00:00:00").toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" })}
                        </span>
                      )}
                      {dismantleDetail.dismantled_by_user && (
                        <span className="text-xs text-zinc-600">de către <strong>{dismantleDetail.dismantled_by_user}</strong></span>
                      )}
                    </div>
                    {dismantleDetail.actual_total_hours > 0 && (
                      <div className="text-xs text-orange-800 font-semibold mb-2 bg-orange-100 inline-block px-2 py-1 rounded">
                        ⏱ <strong>{dismantleDetail.actual_total_hours}h</strong> ore reale logate
                        {dismantleDetail.actual_dates && dismantleDetail.actual_dates.length > 0 && (
                          <span className="font-normal text-orange-700"> · {dismantleDetail.actual_dates.length} {dismantleDetail.actual_dates.length === 1 ? "zi" : "zile"}</span>
                        )}
                      </div>
                    )}
                    {dismantleDetail.persons_actual && dismantleDetail.persons_actual.length > 0 && (
                      <div className="mt-1 flex items-start gap-1.5 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider text-orange-700 font-bold mt-0.5">⏱ Echipa reală (ore):</span>
                        {dismantleDetail.persons_actual.map((p) => (
                          <span
                            key={`act-${p.id}`}
                            className="text-[11px] px-2 py-0.5 rounded font-semibold inline-flex items-center gap-1"
                            style={{ background: p.color || "#FFEDD5", color: p.color ? "white" : "#9A3412" }}
                            title={`${p.hours}h în ${(p.dates || []).join(", ")}`}
                          >
                            {p.name} <span className="text-[9px] font-bold bg-white/40 px-1 rounded">{p.hours}h</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {dismantleDetail.persons_planned && dismantleDetail.persons_planned.length > 0 && (
                      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">📌 Echipa planificată:</span>
                        {dismantleDetail.persons_planned.map((p) => (
                          <span key={`pln-${p.id}`} className="text-[11px] px-2 py-0.5 rounded font-semibold" style={{ background: p.color || "#E5E7EB", color: p.color ? "white" : "#71717A" }}>
                            {p.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {((dismantleDetail.cars_actual || []).length + (dismantleDetail.cars_planned || []).length) > 0 && (
                      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">🚗 Mașini:</span>
                        {[...(dismantleDetail.cars_actual || []), ...(dismantleDetail.cars_planned || []).filter((c) => !(dismantleDetail.cars_actual || []).some((a) => a.id === c.id))].map((c) => (
                          <span key={`car-${c.id}`} className="text-[11px] bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-semibold">
                            {c.label}
                          </span>
                        ))}
                      </div>
                    )}
                    {dismantleDetail.dismantled_note && (
                      <div className="mt-2 text-xs text-zinc-600 italic bg-white/70 px-2 py-1 rounded border border-zinc-200">📝 {dismantleDetail.dismantled_note}</div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2"><Label>Titlu</Label><Input data-testid="proj-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ex: Sanierung Seestrasse 42" /></div>
                  <div className="md:col-span-2">
                    <Label>Categorie</Label>
                    {isPlanificator && !editing ? (
                      <div className="mt-1 px-3 py-2 rounded-md border-2 border-cyan-300 bg-cyan-50 text-sm font-bold text-cyan-900" data-testid="proj-category-locked">
                        6 Sanierungskonzept <span className="font-normal text-xs text-cyan-700">(proiectele adăugate de planificator intră direct aici)</span>
                      </div>
                    ) : (
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="proj-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PROJECT_CATEGORIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div className="md:col-span-2 pt-3 border-t border-[var(--border)]">
                    <div className="small-label">Persoane de contact</div>
                  </div>
                  <div><Label>Auftraggeber</Label><Input data-testid="proj-auftraggeber" value={form.auftraggeber} onChange={(e) => setForm({ ...form, auftraggeber: e.target.value })} placeholder="Firmă sau persoană" /></div>
                  <div><Label>Telefon Auftraggeber</Label><Input value={form.auftraggeber_phone} onChange={(e) => setForm({ ...form, auftraggeber_phone: e.target.value })} /></div>
                  <div className="md:col-span-2"><Label>Persoană de contact (Auftraggeber)</Label><Input data-testid="proj-auftraggeber-contact" value={form.auftraggeber_contact} onChange={(e) => setForm({ ...form, auftraggeber_contact: e.target.value })} placeholder="Nume persoană (dacă Auftraggeber e firmă)" /></div>
                  <div><Label>Bauherr</Label><Input data-testid="proj-bauherr" value={form.bauherr} onChange={(e) => setForm({ ...form, bauherr: e.target.value })} /></div>
                  <div><Label>Telefon Bauherr</Label><Input value={form.bauherr_phone} onChange={(e) => setForm({ ...form, bauherr_phone: e.target.value })} /></div>
                  <div><Label>Bauleiter sau Arhitect</Label><Input data-testid="proj-bauleiter" value={form.bauleiter} onChange={(e) => setForm({ ...form, bauleiter: e.target.value })} /></div>
                  <div><Label>Telefon Bauleiter/Arhitect</Label><Input value={form.bauleiter_phone} onChange={(e) => setForm({ ...form, bauleiter_phone: e.target.value })} /></div>
                  <div className="md:col-span-2"><Label>👷 Vorarbeiter / Șef echipă</Label><Input data-testid="proj-vorarbeiter" value={form.vorarbeiter || ""} onChange={(e) => setForm({ ...form, vorarbeiter: e.target.value })} placeholder="Nume șef echipă pe șantier" /></div>
                  <div className="md:col-span-2"><Label>Mail de contact</Label><Input type="email" data-testid="proj-email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="nume@domeniu.ch" /></div>

                  <div className="md:col-span-2 pt-3 border-t border-[var(--border)]">
                    <div className="small-label">Șantier</div>
                  </div>
                  <div className="md:col-span-2"><Label>Adresă</Label><Input data-testid="proj-adresa" value={form.adresa} onChange={(e) => setForm({ ...form, adresa: e.target.value })} /></div>
                  
                  {/* Lucrări de executat — 2 moduri: 🚀 Rapid (zone + m²/material) sau 🔧 Detaliat (per cameră cu clasificare auto) */}
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Label className="text-base font-display font-bold mb-0">Lucrări de executat</Label>
                      <div className="ml-auto inline-flex rounded-md border border-zinc-300 overflow-hidden text-xs font-semibold" data-testid="scope-mode-toggle">
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, _scope_mode: "rapid" })}
                          className={`px-3 py-1.5 transition-all ${(form._scope_mode || "rapid") === "rapid" ? "bg-emerald-600 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"}`}
                          data-testid="scope-mode-rapid"
                        >
                          🚀 Rapid
                        </button>
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, _scope_mode: "detaliat" })}
                          className={`px-3 py-1.5 border-l border-zinc-300 transition-all ${form._scope_mode === "detaliat" ? "bg-blue-600 text-white" : "bg-white text-zinc-700 hover:bg-zinc-100"}`}
                          data-testid="scope-mode-detaliat"
                        >
                          🔧 Detaliat (per cameră)
                        </button>
                      </div>
                    </div>
                    {(form._scope_mode || "rapid") === "rapid" ? (
                      <>
                        <p className="text-xs text-zinc-500 mb-2">Rapid: introdu numărul de zone EKAS și m² pentru fiecare material de scos.</p>
                        <QuickScopeInput
                          value={form.quick_scope || { ekas_zones: 0, materials: [] }}
                          onChange={(qs) => setForm({ ...form, quick_scope: qs })}
                        />
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-zinc-500 mb-2">Detaliat: adaugă fiecare lucrare cu cameră, material și cantitate. Sistemul calculează automat zonele EKAS / Factsheet / Merkblatt și obligativitatea Luftmessung.</p>
                        <ScopeBuilder
                          value={form.scope_items}
                          onChange={(arr) => setForm({ ...form, scope_items: arr })}
                          isRenovation={form.is_renovation !== false}
                          onIsRenovationChange={(v) => setForm({ ...form, is_renovation: v })}
                        />
                      </>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <Label>Culoare proiect <span className="text-[10px] font-normal text-zinc-400">(folosită în Planificator)</span></Label>
                    <div className="flex gap-2 mt-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, color: "" })}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${!form.color ? "ring-2 ring-zinc-700 bg-white" : "bg-zinc-50 hover:bg-zinc-100"}`}
                        data-testid="proj-color-default"
                      >
                        Auto (categorie)
                      </button>
                      {PROJECT_COLORS.map((c) => (
                        <button
                          key={c.code}
                          type="button"
                          onClick={() => setForm({ ...form, color: c.hex })}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${form.color === c.hex ? "ring-2 ring-offset-1" : "hover:opacity-80"}`}
                          style={{ background: c.hex, color: "#fff" }}
                          data-testid={`proj-color-${c.code}`}
                        >
                          <span className="w-2 h-2 rounded-full bg-white"/>
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div><Label>Minim personal</Label><Input type="number" min={0} placeholder="ex: 3" data-testid="proj-minpeople" value={form.min_people} onChange={(e) => setForm({ ...form, min_people: e.target.value })} /></div>
                  {/* === Workflow măsurătoare post-finalizare (Rückbau / EKAS / Factsheet) === */}
                  {(() => {
                    const zonesArr = Array.isArray(form.zones) ? form.zones : [];
                    const hasEkas = zonesArr.some((z) => (z?.type || "").toUpperCase() === "EKAS");
                    const hasFactsheet = zonesArr.some((z) => (z?.type || "").toUpperCase() === "FACTSHEET");
                    if (!hasEkas && !hasFactsheet) return null;
                    return (
                      <div className="md:col-span-2 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="post-measurement-block">
                        <div className="text-xs font-bold uppercase tracking-wider text-amber-900 mb-2">
                          📐 Măsurătoare post-finalizare
                        </div>
                        {hasEkas && (
                          <div className="mb-3">
                            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={!!form.is_ruckbau}
                                onChange={(e) => setForm({ ...form, is_ruckbau: e.target.checked })}
                                className="accent-amber-600 w-4 h-4"
                                data-testid="proj-is-ruckbau"
                              />
                              <span className="font-semibold">Este Rückbau? <span className="text-zinc-500 font-normal">(șantier cu zone EKAS)</span></span>
                            </label>
                            {!form.is_ruckbau && (
                              <div className="mt-1.5 ml-6 text-[11px] text-emerald-700 font-medium">
                                ✓ EKAS standard — măsurătoare necesară automat în ziua următoare finalizării
                              </div>
                            )}
                            {form.is_ruckbau && (
                              <div className="mt-2 ml-6">
                                <div className="text-[11px] text-zinc-700 mb-1.5">La Rückbau, măsurătoarea este <em>freiwillig</em>:</div>
                                <div className="flex gap-3">
                                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                    <input
                                      type="radio"
                                      name="ruckbau-meas"
                                      checked={form.requires_post_measurement === true}
                                      onChange={() => setForm({ ...form, requires_post_measurement: true })}
                                      data-testid="proj-ruckbau-with-meas"
                                    />
                                    <span>Cu măsurătoare</span>
                                  </label>
                                  <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                    <input
                                      type="radio"
                                      name="ruckbau-meas"
                                      checked={form.requires_post_measurement === false}
                                      onChange={() => setForm({ ...form, requires_post_measurement: false })}
                                      data-testid="proj-ruckbau-without-meas"
                                    />
                                    <span>Fără măsurătoare</span>
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {hasFactsheet && (
                          <div className={hasEkas ? "pt-2 border-t border-amber-200" : ""}>
                            <div className="text-[11px] text-zinc-700 mb-1.5 font-semibold">📋 Factsheet — se cere măsurătoare?</div>
                            <div className="flex gap-3 ml-1">
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                <input
                                  type="radio"
                                  name="factsheet-meas"
                                  checked={form.requires_post_measurement === true}
                                  onChange={() => setForm({ ...form, requires_post_measurement: true })}
                                  data-testid="proj-factsheet-with-meas"
                                />
                                <span>Da, cu măsurătoare</span>
                              </label>
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                <input
                                  type="radio"
                                  name="factsheet-meas"
                                  checked={form.requires_post_measurement === false || form.requires_post_measurement === null || form.requires_post_measurement === undefined}
                                  onChange={() => setForm({ ...form, requires_post_measurement: false })}
                                  data-testid="proj-factsheet-without-meas"
                                />
                                <span>Nu, fără măsurătoare (default)</span>
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div><Label>Status</Label><Input value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} /></div>
                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Început</Label>
                      <Input
                        type="date"
                        data-testid="proj-start-date"
                        value={form.start_date}
                        disabled={form.indeterminate_start}
                        onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                      />
                      <label className="flex items-center gap-2 mt-1.5 text-xs text-zinc-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          data-testid="proj-indeterminate-start"
                          checked={!!form.indeterminate_start}
                          onChange={(e) => setForm({ ...form, indeterminate_start: e.target.checked, start_date: e.target.checked ? "" : form.start_date })}
                          className="accent-[var(--primary)]"
                        />
                        ♾ Dată start nedeterminată (șantier cu început încă nestabilit)
                      </label>
                    </div>
                    <div>
                      <Label>Sfârșit</Label>
                      <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                    </div>
                  </div>
                  {(() => {
                    if (!form.start_date || form.indeterminate_start) return null;
                    const sd = new Date(form.start_date + "T00:00:00");
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const diffDays = Math.floor((sd - today) / (1000 * 60 * 60 * 24));
                    // Eroare: data de start sub -365 zile (peste 1 an în trecut)
                    if (diffDays < -365) {
                      const yearsBack = Math.abs(Math.floor(diffDays / 365));
                      return (
                        <div
                          className="md:col-span-2 bg-red-50 border-2 border-red-500 rounded-lg p-3 flex items-start gap-3 ring-2 ring-red-200"
                          data-testid="proj-past-start-error"
                        >
                          <AlertCircle size={22} className="text-red-600 flex-shrink-0 mt-0.5 animate-pulse" />
                          <div className="text-sm text-red-900">
                            <div className="font-bold mb-0.5">⛔ Modifică data — start prea îndepărtat în trecut</div>
                            <div className="text-red-800">
                              Data de start <strong>{fmtRoDate(form.start_date)}</strong> este cu <strong>{Math.abs(diffDays)} zile</strong> (~{yearsBack} {yearsBack === 1 ? "an" : "ani"}) în trecut. Nu poți crea un șantier cu start atât de îndepărtat. <strong>Modifică data te rog.</strong>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    if (diffDays <= 30) return null;
                    return (
                      <div
                        className="md:col-span-2 bg-amber-50 border-2 border-amber-400 rounded-lg p-3 flex items-start gap-3"
                        data-testid="proj-far-start-warning"
                      >
                        <AlertCircle size={22} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-900">
                          <div className="font-bold mb-0.5">Ești sigur(ă) că șantierul începe în luna pe care ai introdus-o?</div>
                          <div className="text-amber-800">
                            Data de start este peste <strong>{diffDays} zile</strong> ({Math.round(diffDays / 30)} {Math.round(diffDays / 30) === 1 ? "lună" : "luni"}) — <strong>{fmtRoDate(form.start_date)}</strong>. Verifică te rog data și confirmă la salvare.
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {form.category === "1" && (
                    <div className="md:col-span-2 bg-amber-50 border border-amber-200 rounded p-3">
                      <Label className="text-amber-900">📅 Deadline ofertare (data limită până la care trebuie trimisă oferta)</Label>
                      <Input type="date" data-testid="proj-oferta-deadline" value={form.oferta_deadline} onChange={(e) => setForm({ ...form, oferta_deadline: e.target.value })} className="mt-1 bg-white"/>
                      <p className="text-[11px] text-amber-700 mt-1">Va apărea ca banner cu zilele rămase pe cardul proiectului.</p>
                    </div>
                  )}

                  <div className="md:col-span-2 pt-3 border-t border-[var(--border)]">
                    <div className="small-label">Observații</div>
                  </div>
                  <div className="md:col-span-2"><Label>Special</Label><Textarea rows={2} data-testid="proj-special" value={form.special} onChange={(e) => setForm({ ...form, special: e.target.value })} placeholder="Cerințe speciale, echipamente, restricții..." /></div>
                  <ProjectUtilitiesSection value={form.utilities} onChange={(u) => setForm({ ...form, utilities: u })} />
                  <div className="md:col-span-2"><Label>Descriere generală</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                </div>
                <DialogFooter className="gap-2 flex-wrap">
                  {!editing && draftSavedAt && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!window.confirm("Renunți la ciornă? Datele introduse până acum vor fi șterse.")) return;
                        clearDraft();
                        setForm(EMPTY_PROJ);
                        setOpen(false);
                        toast.success("Ciornă ștearsă");
                      }}
                      className="text-red-600 hover:bg-red-50 border-red-200"
                      data-testid="proj-discard-draft"
                    >
                      🗑 Renunță la ciornă
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (!editing && draftSavedAt) {
                        // Închidem dar PĂSTRĂM ciorna în localStorage
                        setOpen(false);
                        toast("📝 Ciorna a fost păstrată — o vei găsi la următoarea deschidere", { duration: 2500 });
                        return;
                      }
                      setOpen(false);
                      setEditing(null);
                      setForm(EMPTY_PROJ);
                    }}
                    data-testid="proj-cancel"
                  >
                    {(!editing && draftSavedAt) ? "Închide (păstrează ciorna)" : "Anulează"}
                  </Button>
                  <Button data-testid="submit-project" onClick={submit} className="bg-[var(--primary)]">Salvează</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Banner avertisment duplicate */}
      {visibleDuplicateGroups.length > 0 && (
        <div
          className="card-flat p-3 mb-4 bg-red-50 border-2 border-red-300 ring-2 ring-red-100"
          data-testid="duplicate-projects-banner"
        >
          <div className="flex items-start gap-3">
            <AlertCircle size={22} className="text-red-600 flex-shrink-0 mt-0.5 animate-pulse" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-extrabold text-red-900 uppercase tracking-wider">
                ⚠ Posibile duplicate detectate · {visibleDuplicateGroups.length} {visibleDuplicateGroups.length === 1 ? "grup" : "grupuri"}
              </div>
              <div className="text-xs text-red-800 mt-1">
                Există proiecte cu aceeași locație și perioadă suprapusă. Verifică și șterge duplicatele:
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {visibleDuplicateGroups.map((g) => (
                  <button
                    key={g.key}
                    onClick={() => setDuplicateModalKey(g.key)}
                    className="text-xs px-3 py-1.5 rounded-full bg-red-100 hover:bg-red-200 text-red-900 font-semibold border border-red-300 transition-colors"
                    data-testid={`duplicate-group-${g.key}`}
                  >
                    📍 {g.label} ({g.projects.length} variante)
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal comparare duplicate */}
      {duplicateModalKey && (() => {
        const grp = visibleDuplicateGroups.find((g) => g.key === duplicateModalKey);
        if (!grp) return null;
        return (
          <Dialog open onOpenChange={(v) => { if (!v) setDuplicateModalKey(null); }}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertCircle className="text-red-600" size={20} />
                  Posibile duplicate · {grp.label}
                </DialogTitle>
              </DialogHeader>
              <div className="text-xs text-zinc-600 mb-3">
                Acestea sunt {grp.projects.length} proiecte cu aceeași locație și perioadă suprapusă. Verifică datele și șterge duplicatele care nu sunt necesare.
              </div>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {grp.projects.map((p) => (
                  <div key={p.id} className="border-2 border-zinc-200 rounded-lg p-3 bg-zinc-50" data-testid={`duplicate-project-${p.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-base">{p.name}</div>
                        <div className="text-xs text-zinc-600 mt-1 grid grid-cols-2 gap-x-4 gap-y-1">
                          <div>📍 <strong>Adresă:</strong> {p.adresa || "—"}</div>
                          <div>🏢 <strong>Auftraggeber:</strong> {p.auftraggeber || "—"}</div>
                          <div>📅 <strong>Start:</strong> {fmtRoDate(p.start_date) || (p.indeterminate_start ? "TBD" : "—")}</div>
                          <div>📅 <strong>Sfârșit:</strong> {p.end_date || "—"}</div>
                          <div>👥 <strong>Necesar:</strong> {p.min_people || 0}{p.max_people && p.max_people !== p.min_people ? `–${p.max_people}` : ""}</div>
                          <div>🏷 <strong>Categorie:</strong> {p.category || "—"} · {p.status || "—"}</div>
                        </div>
                        {p.description && <div className="text-xs text-zinc-500 mt-1 italic line-clamp-2">{p.description}</div>}
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setDuplicateModalKey(null); edit(p); }}
                          data-testid={`duplicate-edit-${p.id}`}
                          className="text-xs"
                        >
                          ✏️ Editează
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            const ok = await confirm({
                              title: `Șterge „${p.name}"?`,
                              body: `Vei șterge DEFINITIV proiectul „${p.name}" împreună cu toate alocările și documentele asociate. Această acțiune NU poate fi anulată.`,
                              confirmText: "DA, șterge definitiv",
                              cancelText: "Anulează",
                              danger: true,
                            });
                            if (!ok) return;
                            try {
                              await api.delete(`/projects/${p.id}`);
                              toast.success(`Proiect șters: ${p.name}`);
                              setDuplicateModalKey(null);
                              load();
                            } catch (e) { toast.error(e?.response?.data?.detail || "Eroare ștergere"); }
                          }}
                          className="border-red-300 text-red-700 hover:bg-red-50 text-xs"
                          data-testid={`duplicate-delete-${p.id}`}
                        >
                          <Trash2 size={12} className="mr-1"/> Șterge
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <DialogFooter className="gap-2 flex-wrap">
                <Button
                  variant="outline"
                  className="border-emerald-500 text-emerald-700 hover:bg-emerald-50 font-bold"
                  onClick={() => dismissDuplicateGroup(grp)}
                  data-testid={`duplicate-confirm-ok-${grp.key}`}
                  title="Marchează acest grup ca verificat — nu sunt duplicate"
                >
                  ✓ Confirmă: nu sunt duplicate
                </Button>
                <Button variant="outline" onClick={() => setDuplicateModalKey(null)}>Închide</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Search bar */}
      <div className="card-flat p-3 mb-4 flex items-center gap-2">
        <Search size={16} className="text-zinc-400" />
        <Input
          placeholder="Caută proiect sau regie după nume, firmă, adresă, descriere..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border-0 shadow-none focus-visible:ring-0"
          data-testid="proiecte-search"
        />
      </div>

      {/* Tab-uri mari pentru separare vizibilă Proiecte vs Regii */}
      <div className="flex items-center gap-2 mb-4 rounded-lg overflow-hidden border-2 border-zinc-200 bg-white p-1" data-testid="section-tabs">
        <button
          type="button"
          onClick={() => setSectionTab("projects")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md font-bold text-sm transition-all ${sectionTab === "projects" ? "bg-gradient-to-br from-[var(--primary)] to-[var(--primary-dark)] text-white shadow-md" : "text-zinc-600 hover:bg-zinc-50"}`}
          data-testid="section-tab-projects"
        >
          <span className="text-lg">🏗</span>
          <span>PROIECTE</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${sectionTab === "projects" ? "bg-white/25" : "bg-zinc-100 text-zinc-600"}`}>{filteredRows.length}</span>
        </button>
        <button
          type="button"
          onClick={() => setSectionTab("regii")}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-md font-bold text-sm transition-all ${sectionTab === "regii" ? "bg-gradient-to-br from-[var(--orange-bila)] to-orange-600 text-white shadow-md" : "text-zinc-600 hover:bg-zinc-50"}`}
          data-testid="section-tab-regii"
        >
          <span className="text-lg">⚙</span>
          <span>REGII</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${sectionTab === "regii" ? "bg-white/25" : "bg-zinc-100 text-zinc-600"}`}>{filteredRegii.length}</span>
        </button>
        <button
          type="button"
          onClick={() => setSectionTab("both")}
          className={`px-3 py-3 rounded-md text-xs font-semibold transition-all whitespace-nowrap ${sectionTab === "both" ? "bg-zinc-800 text-white" : "text-zinc-500 hover:bg-zinc-50"}`}
          title="Arată ambele secțiuni în același ecran"
          data-testid="section-tab-both"
        >
          📋 Ambele
        </button>
      </div>

      {/* Regii section - compact, grupate pe firmă în ordinea descrescătoare a numărului de regii */}
      {(sectionTab === "regii" || sectionTab === "both") && filteredRegii.length > 0 && (() => {
        const todayISO = new Date().toISOString().slice(0, 10);
        // Regie „încheiată" = explicit inactivă SAU cu end_date fix expirat
        const isEnded = (r) => (r.active === false) || (!!r.end_date && !r.indeterminate_end && r.end_date < todayISO);
        // Grupează după firma (case-insensitive); "(Fără firmă)" pentru cele goale.
        const groups = new Map();
        for (const r of filteredRegii) {
          const key = (r.firma || "").trim() || "(Fără firmă)";
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(r);
        }
        // Sortare în interiorul grupului: activele înainte, încheiatele la final (dar păstrează ordinea alfabetică în interior)
        for (const [, arr] of groups) {
          arr.sort((a, b) => {
            const ea = isEnded(a) ? 1 : 0;
            const eb = isEnded(b) ? 1 : 0;
            if (ea !== eb) return ea - eb;
            return (a.name || "").localeCompare(b.name || "");
          });
        }
        // Sortare: descrescător după count, apoi alfabetic pe firma
        const ordered = [...groups.entries()].sort((a, b) => {
          if (b[1].length !== a[1].length) return b[1].length - a[1].length;
          return a[0].localeCompare(b[0]);
        });
        return (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Briefcase size={16} className="text-[var(--orange-bila)]" />
            <h2 className="font-display text-base font-bold">Regii</h2>
            <span className="text-xs text-zinc-500">({filteredRegii.length})</span>
          </div>
          <div className="space-y-4" data-testid="regii-firma-groups">
            {ordered.map(([firma, regiiList]) => (
              <div key={firma} data-testid={`regii-firma-group-${firma}`}>
                <div className="flex items-center gap-2 mb-2 pb-1 border-b border-zinc-200">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">🏢 {firma}</span>
                  <span className="text-[10px] font-semibold text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded-full">{regiiList.length}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5">
                  {[...regiiList].sort((a, b) => (b.active_this_week ? 1 : 0) - (a.active_this_week ? 1 : 0)).map((r) => {
              // Considerăm regia „încheiată" în 2 cazuri:
              // 1. Marcată explicit inactivă (r.active === false)
              // 2. Are dată de finalizare fixă (nu indeterminate) și data a trecut deja
              const todayISO = new Date().toISOString().slice(0, 10);
              const dateExpired = !!(r.end_date && !r.indeterminate_end && r.end_date < todayISO);
              const inactive = r.active === false || dateExpired;
              const activeWeek = !!r.active_this_week && !inactive;
              return (
                <div
                  key={r.id}
                  onClick={(e) => {
                    const tgt = e.target;
                    if (tgt.closest && tgt.closest("button, a, input, label, select, textarea, [role='button']")) return;
                    setSelectedCardId(`regie-${r.id}`);
                    saveScroll();
                    setRegieDetailFor(r);
                  }}
                  className={`card-flat p-3 flex flex-col gap-2 group hover:border-[var(--orange-bila)] transition-all cursor-pointer ${inactive ? "regie-card-ended" : ""} ${
                    activeWeek ? "border-emerald-400 ring-1 ring-emerald-300 shadow-md bg-emerald-50/60" : !inactive ? "opacity-60 saturate-50" : ""
                  } ${
                    selectedCardId === `regie-${r.id}`
                      ? "ring-4 ring-blue-500 ring-offset-2 border-blue-500 shadow-lg"
                      : highlightId === r.id ? "ring-4 ring-amber-400 ring-offset-2 shadow-lg" : ""
                  }`}
                  data-testid={`regie-card-${r.id}`}
                >
                  <div className="flex gap-2.5">
                  <span
                    className="w-2 min-w-[0.5rem] rounded-full flex-shrink-0"
                    style={{ background: r.color || "#F59E0B", opacity: inactive ? 0.4 : 1 }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate flex items-center gap-1" title={r.name}>
                      {r.name}
                      {activeWeek && (
                        <span className="text-[8px] px-1 py-0.5 rounded bg-emerald-500 text-white font-bold flex-shrink-0" title="Se lucrează aici săptămâna aceasta" data-testid={`regie-active-badge-${r.id}`}>
                          ● ACTIVĂ
                        </span>
                      )}
                      {inactive && (
                        <span
                          className="text-[8px] px-1 py-0.5 rounded bg-zinc-200 text-zinc-600 font-bold flex-shrink-0"
                          title={dateExpired && r.active !== false ? `Data de finalizare ${r.end_date} a trecut` : "Marcată inactivă"}
                          data-testid={`regie-ended-badge-${r.id}`}
                        >
                          ÎNCHEIATĂ
                        </span>
                      )}
                    </div>
                    {r.firma && <div className="text-[11px] text-zinc-600 truncate" title={r.firma}>{r.firma}</div>}
                    {r.adresa && <div className="text-[10px] text-zinc-400 truncate" title={r.adresa}>{r.adresa}</div>}
                    {r.vorarbeiter && (
                      <div className="text-[10px] text-zinc-600 truncate font-medium" title={`Vorarbeiter: ${r.vorarbeiter}`}>
                        👷 {r.vorarbeiter}
                      </div>
                    )}
                    {r.indeterminate_end && <div className="text-[10px] text-emerald-600 font-medium mt-0.5">♾ Nedeterminat</div>}
                    {(r.default_min_people || r.default_max_people) && (
                      <div className="text-[10px] text-zinc-500 mt-0.5">👥 {r.default_min_people || 0}-{r.default_max_people || 0} personal/zi</div>
                    )}
                    <div className="mt-1.5 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {canDo("PROIECTE", "edit") && <button onClick={(e) => { e.stopPropagation(); saveScroll(); editRegie(r); }} className="text-[10px] text-[var(--primary)] hover:underline">Editează</button>}
                      {canDo("PROIECTE", "edit") && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openDiag(r); }}
                          className="text-[10px] text-sky-600 hover:underline"
                          data-testid={`regie-diagnose-${r.id}`}
                          title="De ce (nu) apare în Planificator?"
                        >
                          🩺 Diagnostic
                        </button>
                      )}
                      {canDo("PROIECTE", "edit") && r.indeterminate_end && (
                        <button
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/regii/${r.id}/extend-year`);
                              toast.success(`+${data.weeks_added} săpt. pentru ${data.year}`);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Eroare");
                            }
                          }}
                          className="text-[10px] text-emerald-600 hover:underline"
                          data-testid={`regie-extend-${r.id}`}
                          title="Extinde pe anul următor"
                        >
                          📅 Extinde
                        </button>
                      )}
                      {canDo("PROIECTE", "delete") && <button onClick={() => removeRegie(r)} className="text-red-600"><Trash2 size={12}/></button>}
                    </div>
                  </div>
                  </div>
                  {!inactive && (() => {
                    const missing = missingRegieFields(r);
                    if (missing.length === 0) return null;
                    return (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); if (canDo("PROIECTE", "edit")) { saveScroll(); editRegie(r); } }}
                        className="px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800 flex items-start gap-1.5 hover:bg-amber-100 transition-colors text-left w-full"
                        data-testid={`regie-missing-banner-${r.id}`}
                        title={`Lipsesc: ${missing.map((m) => m.label).join(", ")}`}
                      >
                        <AlertCircle size={11} className="shrink-0 mt-0.5 text-amber-600"/>
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] font-bold uppercase tracking-wider">
                            Date lipsă ({missing.length})
                          </div>
                          <div className="text-[9px] truncate">
                            {missing.map((m) => m.label).join(", ")}
                          </div>
                        </div>
                      </button>
                    );
                  })()}
                </div>
              );
            })}
                </div>
              </div>
            ))}
          </div>
        </div>
        );
      })()}

      {(sectionTab === "projects" || sectionTab === "both") && (
      <>
      <div className="card-flat p-3 mb-3 flex items-center gap-2 overflow-x-auto" data-testid="status-filter-bar">
        <span className="text-[11px] text-zinc-500 font-bold uppercase tracking-wide whitespace-nowrap mr-1">Status:</span>
        {[
          { key: "pe_rol",      label: "📋 Pe rol",       cls: "bg-blue-50 text-blue-700 border-blue-200", active: "bg-blue-600 text-white border-blue-600", tip: "Șantiere active de care ne ocupăm (de la De Ofertat până la Sanierungskonzept) — nefinalizate" },
          { key: "in_lucru",    label: "🚧 În lucru",     cls: "bg-emerald-50 text-emerald-700 border-emerald-200", active: "bg-emerald-600 text-white border-emerald-600" },
          { key: "de_demontat", label: "🔧 De demontat",  cls: "bg-orange-50 text-orange-700 border-orange-200", active: "bg-orange-600 text-white border-orange-600" },
          { key: "demontat",    label: "✅ Demontate",    cls: "bg-zinc-50 text-zinc-700 border-zinc-200", active: "bg-zinc-700 text-white border-zinc-700" },
          { key: "all",         label: "Toate",            cls: "bg-zinc-100 text-zinc-700 border-zinc-200", active: "bg-[var(--primary)] text-white border-[var(--primary)]" },
        ].map((b) => (
          <button
            key={b.key}
            onClick={() => setStatusFilter(b.key)}
            className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap font-semibold border transition-colors ${statusFilter === b.key ? b.active : `${b.cls} hover:ring-1 hover:ring-zinc-300`}`}
            data-testid={`status-info-${b.key}`}
            title={b.tip || `Arată doar proiectele: ${b.label}`}
          >
            {b.label} ({counts[b.key] || 0})
          </button>
        ))}
      </div>

      <div className="flex items-start gap-3 mb-6">
        <div className="card-flat p-3 flex-1 flex flex-wrap items-center gap-2" data-testid="project-categories-bar">
          <button onClick={() => setFilterCat("all")} className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap font-semibold transition-colors ${filterCat === "all" ? "bg-[var(--primary)] text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>
            Toate ({rows.length})
          </button>
          {PROJECT_CATEGORIES.map((c) => (
            <button key={c.code} onClick={() => setFilterCat(c.code)} className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap font-semibold transition-colors ${filterCat === c.code ? "bg-[var(--primary)] text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}>
              {c.label} ({(grouped[c.code] || []).length})
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 border border-[var(--border)] rounded-md p-1 bg-white shadow-sm flex-shrink-0" data-testid="view-mode-toggle">
          <button
            onClick={() => { setViewMode("cards"); localStorage.setItem("proiecte-view", "cards"); }}
            className={`text-xs px-3 py-1.5 rounded font-semibold transition-colors flex items-center gap-1.5 ${viewMode === "cards" ? "bg-[var(--primary)] text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
            data-testid="view-mode-cards"
            title="Vizualizare carduri"
          >
            <LayoutGrid size={14}/> Carduri
          </button>
          <button
            onClick={() => { setViewMode("list"); localStorage.setItem("proiecte-view", "list"); }}
            className={`text-xs px-3 py-1.5 rounded font-semibold transition-colors flex items-center gap-1.5 ${viewMode === "list" ? "bg-[var(--primary)] text-white" : "text-zinc-700 hover:bg-zinc-100"}`}
            data-testid="view-mode-list"
            title="Vizualizare listă"
          >
            <ListIcon size={14}/> Listă
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {visibleCategories.length === 0 && <div className="text-sm text-zinc-500">Niciun proiect în această categorie.</div>}
        {visibleCategories.map((cat) => {
          const items = grouped[cat.code] || [];
          if (items.length === 0) return null;
          return (
            <div key={cat.code}>
              <div className="flex items-center gap-2 mb-3">
                <Folder size={18} className="text-[var(--primary)]"/>
                <h2 className="font-display text-xl font-bold">{cat.label}</h2>
                <span className="text-xs text-zinc-500">({items.length})</span>
              </div>
              <div className={viewMode === "list" ? "card-flat overflow-hidden" : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"}>
                {viewMode === "list" && (
                  <div className="hidden md:grid grid-cols-[1fr_180px_180px_140px_140px_120px] gap-3 px-4 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider font-bold text-zinc-500">
                    <div>Proiect</div>
                    <div>Auftraggeber</div>
                    <div>Bauleiter</div>
                    <div>Perioadă</div>
                    <div>Status</div>
                    <div className="text-right">Acțiuni</div>
                  </div>
                )}
                {items.map((r) => viewMode === "list" ? <ProjectListRow key={r.id} r={r} highlightId={highlightId} selectedCardId={selectedCardId} onSelect={() => { setSelectedCardId(r.id); saveScroll(); setDetailFor(r); }} canDo={canDo} setDocsFor={(x) => { saveScroll(); setDocsFor(x); }} setHoursReportFor={(x) => { saveScroll(); setHoursReportFor(x); }} edit={(x) => { saveScroll(); edit(x); }} remove={remove} markFinalizat={markFinalizat} markDemontat={markDemontat} reopenProject={reopenProject} siteCount={siteCounts[r.id]} navigate={navigate} /> : (
                  <div
                    key={r.id}
                    onClick={(e) => {
                      // Ignore clicks that originated from interactive children
                      const tgt = e.target;
                      if (tgt.closest && tgt.closest("button, a, input, label, select, textarea, [role='button']")) return;
                      setSelectedCardId(r.id);
                      saveScroll();
                      setDetailFor(r);
                    }}
                    className={`card-flat p-5 transition-all cursor-pointer ${
                      selectedCardId === r.id
                        ? "ring-4 ring-blue-500 ring-offset-2 border-blue-500 shadow-lg"
                        : highlightId === r.id ? "ring-4 ring-amber-400 ring-offset-2 shadow-lg" : ""
                    }`}
                    data-testid={`project-card-${r.id}`}
                  >
                    {(() => {
                      const missing = missingProjectFields(r);
                      if (missing.length === 0) return null;
                      return (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); if (canDo("PROIECTE", "edit")) { saveScroll(); edit(r); } }}
                          className="w-full mb-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 flex items-start gap-2 hover:bg-amber-100 transition-colors text-left"
                          data-testid={`project-missing-banner-${r.id}`}
                          title={`Lipsesc: ${missing.map((m) => m.label).join(", ")}`}
                        >
                          <AlertCircle size={14} className="shrink-0 mt-0.5 text-amber-600"/>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-bold uppercase tracking-wider">
                              Date lipsă · introdu {missing.length === 1 ? "ce lipsește" : `cele ${missing.length} elemente lipsă`}
                            </div>
                            <div className="text-[11px] mt-0.5 truncate">
                              {missing.map((m) => m.label).join(", ")}
                            </div>
                          </div>
                          <span className="text-[10px] font-bold text-amber-700 shrink-0">Editează →</span>
                        </button>
                      );
                    })()}
                    <PipelineBanner project={r} onChanged={load} canEdit={canDo("PROIECTE", "edit")} />
                    <div className="flex items-start justify-between">
                      <Folder size={22} className="text-[var(--primary)]" />
                      <div className="flex items-center gap-2">
                        {r.min_people > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--orange-bila-bg)] text-[var(--orange-bila)] font-semibold" title="Minim · Maxim necesar de oameni">
                            {(r.max_people && r.max_people !== r.min_people) ? `${r.min_people}-${r.max_people}` : r.min_people} pers.
                          </span>
                        )}
                        {(() => {
                          const today = new Date().toISOString().slice(0, 10);
                          const sb = statusBucket(r);
                          const notStarted = sb === "in_lucru" && r.start_date && r.start_date > today;
                          if (notStarted) {
                            const days = Math.ceil((new Date(r.start_date).setHours(0,0,0,0) - new Date(today).setHours(0,0,0,0)) / (1000*60*60*24));
                            const isImminent = days <= 7;
                            return (
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full font-semibold ${isImminent ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}
                                title={`Începe la ${fmtRoDate(r.start_date)}`}
                                data-testid={`project-not-started-${r.id}`}
                              >
                                ⏳ Începe în {days} {days === 1 ? "zi" : "zile"}
                              </span>
                            );
                          }
                          return (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                              sb === "de_demontat" ? "bg-orange-100 text-orange-700 animate-pulse"
                              : sb === "demontat" ? "bg-zinc-100 text-zinc-600"
                              : "bg-[var(--primary-subtle)] text-[var(--primary)]"
                            }`}>{r.status}</span>
                          );
                        })()}
                        {(() => {
                          const sc = siteCounts[r.id];
                          const onSiteN = (sc && sc.on_site) || 0;
                          if (statusBucket(r) !== "de_demontat" || onSiteN <= 0) return null;
                          return (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigate("/inventar/santier"); }}
                              className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-orange-600 text-white hover:bg-orange-700 transition-colors flex items-center gap-1 animate-pulse"
                              title="Echipamente lăsate pe șantier — click pentru audit"
                              data-testid={`project-card-leftovers-${r.id}`}
                            >
                              🚧 {onSiteN} de luat
                            </button>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="font-display text-lg font-bold mt-4">{r.name}</div>
                    {r.adresa && <div className="text-xs text-zinc-500 mt-1">📍 {r.adresa}</div>}
                    <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5 flex-wrap">
                      <span>{fmtRoDate(r.start_date) || (r.indeterminate_start ? "TBD" : "—")} → {fmtRoDate(r.end_date) || "—"}</span>
                      {r.indeterminate_start && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold border border-amber-300" title="Dată de start nestabilită">♾ Start TBD</span>
                      )}
                    </div>

                    {(r.auftraggeber || r.bauherr || r.bauleiter) && (
                      <div className="mt-3 space-y-0.5 text-xs text-zinc-600">
                        {r.auftraggeber && <div><span className="text-zinc-400">Auftraggeber:</span> {r.auftraggeber}{r.auftraggeber_phone ? ` · ${r.auftraggeber_phone}` : ""}{r.auftraggeber_contact ? ` · 👤 ${r.auftraggeber_contact}` : ""}</div>}
                        {r.bauherr && <div><span className="text-zinc-400">Bauherr:</span> {r.bauherr}{r.bauherr_phone ? ` · ${r.bauherr_phone}` : ""}</div>}
                        {r.bauleiter && <div><span className="text-zinc-400">Bauleiter/Arhitect:</span> {r.bauleiter}{r.bauleiter_phone ? ` · ${r.bauleiter_phone}` : ""}</div>}
                      </div>
                    )}

                    {r.work_zones && <div className="text-sm text-zinc-700 mt-3 line-clamp-2">{r.work_zones}</div>}
                    {/* Badge zone + materiale — prioritar din quick_scope (mod rapid), altfel calculat din scope_items */}
                    {(() => {
                      const qs = r.quick_scope;
                      const hasQuick = qs && ((qs.ekas_zones || 0) > 0 || (qs.materials || []).length > 0);
                      if (hasQuick) {
                        const ekasCount = Number(qs.ekas_zones || 0);
                        const mats = qs.materials || [];
                        const totalQty = mats.reduce((s, m) => s + (Number(m.qty || 0)), 0);
                        return (
                          <div className="mt-2 space-y-1" data-testid={`proj-quick-scope-${r.id}`}>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {ekasCount > 0 && (
                                <span className="text-[10px] px-2 py-0.5 rounded font-extrabold uppercase tracking-wider bg-blue-600 text-white">
                                  🟦 {ekasCount} zon{ekasCount === 1 ? "ă" : "e"} EKAS
                                </span>
                              )}
                              {totalQty > 0 && (
                                <span className="text-[10px] px-2 py-0.5 rounded font-extrabold uppercase tracking-wider bg-zinc-700 text-white">
                                  ∑ {totalQty.toFixed(2)} m² total
                                </span>
                              )}
                            </div>
                            {mats.length > 0 && (
                              <div className="text-[11px] text-zinc-600 leading-tight">
                                {mats.map((m, i) => (
                                  <span key={i} className="inline-block mr-2">
                                    <span className="font-semibold text-zinc-800">{Number(m.qty || 0).toFixed(m.qty && m.qty % 1 === 0 ? 0 : 2)}{m.unit || "m²"}</span>
                                    <span className="text-zinc-500"> {m.label || m.material}</span>
                                    {m.location && <span className="text-blue-700 font-semibold"> @ {m.location}</span>}
                                    {i < mats.length - 1 && <span className="text-zinc-300"> · </span>}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }
                      // Fallback: mod detaliat cu scope_items
                      if (!Array.isArray(r.scope_items) || r.scope_items.length === 0) return null;
                      const zoneSet = new Set();
                      let totalQty = 0;
                      r.scope_items.forEach((it) => {
                        zoneSet.add(Number(it.zone_index) > 0 ? Number(it.zone_index) : 1);
                        const q = Number(it.qty || 0);
                        if (!Number.isNaN(q)) totalQty += q;
                      });
                      const nz = zoneSet.size;
                      return (
                        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`proj-zones-badge-${r.id}`}>
                          <span className="text-[10px] px-2 py-0.5 rounded font-extrabold uppercase tracking-wider bg-blue-600 text-white">
                            🟦 {nz} zon{nz === 1 ? "ă" : "e"}
                          </span>
                          {totalQty > 0 && (
                            <span className="text-[10px] px-2 py-0.5 rounded font-extrabold uppercase tracking-wider bg-zinc-700 text-white">
                              ∑ {totalQty.toFixed(2)} u total
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    {r.special && <div className="text-xs text-[var(--red-bila)] font-semibold mt-2 line-clamp-2">⚠ {r.special}</div>}

                    <div className="mt-4 flex gap-2 justify-end items-center flex-wrap">
                      {((r.bags_festgebunden || 0) > 0 || (r.bags_schwachgebunden || 0) > 0) && (
                        <span className="text-[10px] text-zinc-500 mr-auto inline-flex items-center gap-1" title="Saci aruncați">
                          <Package2 size={11}/>
                          <span className="text-yellow-600 font-semibold">{r.bags_festgebunden || 0}f</span>
                          <span className="text-zinc-400">/</span>
                          <span className="text-red-600 font-semibold">{r.bags_schwachgebunden || 0}s</span>
                        </span>
                      )}
                      <button onMouseDown={saveScroll} onClick={(e) => { e.stopPropagation(); saveScroll(); setDocsFor(r); }} data-testid={`docs-${r.id}`} className="text-[var(--primary)] text-sm hover:underline inline-flex items-center gap-1">
                        <FileText size={13}/> Documente
                      </button>
                      <button onMouseDown={saveScroll} onClick={(e) => { e.stopPropagation(); saveScroll(); setHoursReportFor(r); }} className="text-xs text-zinc-600 hover:text-[var(--primary)] inline-flex items-center gap-1" data-testid={`hours-btn-${r.id}`} title="Vezi ore lucrate pe acest proiect">
                        ⏱️ Ore lucrate
                      </button>
                      {canDo("PROIECTE", "edit") && <button onMouseDown={saveScroll} onClick={(e) => { e.stopPropagation(); saveScroll(); edit(r); }} className="text-[var(--primary)] text-sm hover:underline">Editează</button>}
                      {canDo("PROIECTE", "edit") && statusBucket(r) === "in_lucru" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); markFinalizat(r); }}
                          className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                          data-testid={`mark-finalizat-${r.id}`}
                          title="Marchează șantierul ca finalizat — trece la 'De demontat'"
                        >🏁 Finalizează</button>
                      )}
                      {canDo("PROIECTE", "edit") && statusBucket(r) === "de_demontat" && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); markDemontat(r); }}
                            className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded bg-zinc-700 text-white hover:bg-zinc-800 transition-colors inline-flex items-center gap-1"
                            data-testid={`mark-demontat-${r.id}`}
                            title="Demontat — trece în categoria 7 (de facturat)"
                          >
                            🔧 Demontează
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); reopenProject(r); }}
                            className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-colors inline-flex items-center gap-1"
                            data-testid={`reopen-project-${r.id}`}
                            title="Trimite proiectul înapoi la 'În lucru'"
                          >
                            <RotateCcw size={11}/> Trimite în lucru
                          </button>
                        </>
                      )}
                      {canDo("PROIECTE", "delete") && <button onClick={() => remove(r.id)} className="text-red-600 p-1 hover:bg-red-50 rounded"><Trash2 size={14}/></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      </>
      )}

      {docsFor && (
        <ProjectDocsDialog
          project={docsFor}
          onClose={() => { setDocsFor(null); restoreScroll(); }}
          onProjectUpdated={(p) => { setDocsFor(p); load(); }}
          canEdit={canDo("PROIECTE", "edit")}
        />
      )}

      {hoursReportFor && (
        <HoursReportDialog project={hoursReportFor} onClose={() => { setHoursReportFor(null); restoreScroll(); }} />
      )}

      {detailFor && (
        <ProjectDetailDialog
          project={detailFor}
          onClose={() => { setDetailFor(null); restoreScroll(); }}
          onOpenHours={() => { const p = detailFor; setDetailFor(null); setHoursReportFor(p); }}
        />
      )}

      {regieDetailFor && (
        <RegieDetailDialog
          regie={regieDetailFor}
          onClose={() => { setRegieDetailFor(null); restoreScroll(); }}
        />
      )}

      {/* Confirm "Finalizat" dialog (DA / NU) */}
      <Dialog open={!!finalizingProject} onOpenChange={(v) => !v && !finalizingBusy && setFinalizingProject(null)}>
        <DialogContent className="max-w-md" data-testid="finalizat-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              🏁 Confirmare finalizare
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-zinc-700 leading-relaxed">
              Confirmi că proiectul <span className="font-bold text-zinc-900">"{finalizingProject?.name}"</span> este finalizat?
            </p>
            <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded text-[12px] text-orange-800">
              ℹ Statusul șantierului se va schimba din <b>"În lucru"</b> în <b>"De demontat"</b>.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setFinalizingProject(null)}
              disabled={finalizingBusy}
              className="border-zinc-300 px-8"
              data-testid="finalizat-confirm-no"
            >
              ✗ NU
            </Button>
            <Button
              onClick={confirmMarkFinalizat}
              disabled={finalizingBusy}
              className="bg-orange-600 hover:bg-orange-700 px-8 font-bold"
              data-testid="finalizat-confirm-yes"
            >
              {finalizingBusy ? "Se procesează…" : "✓ DA, finalizează"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmare „Demontează" — selectează echipa reală + mașină + dată + materiale */}
      {dismantleConfirm && (
        <DismantleDialog
          target={{ label: dismantleConfirm.project.name, day: new Date().toISOString().slice(0, 10) }}
          persons={dismantleConfirm.persons}
          cars={dismantleConfirm.cars}
          projectId={dismantleConfirm.project.id}
          projectColor={dismantleConfirm.project.color}
          onClose={() => setDismantleConfirm(null)}
          onSubmit={submitDismantle}
          busy={dismantleBusy}
        />
      )}    </div>
  );
}

